import React, { useState, useEffect, useRef } from 'react';
import {
  FaBoxOpen,
  FaDownload,
  FaPlus,
  FaSearch,
  FaFilter,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaExchangeAlt,
  FaArrowsAltH,
  FaHistory,
  FaExclamationTriangle,
  FaTags,
  FaTrash,
  FaCubes,
  FaTools,
  FaInfoCircle,
  FaWarehouse,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaClipboardList,
  FaCheckCircle,
  FaLayerGroup,
  FaUsers,
  FaUser,
  FaUserSlash,
  FaUserTag,
  FaChartBar
} from 'react-icons/fa';
import { collection, getDocs, addDoc, doc, updateDoc, getDoc, query, where, orderBy, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebaseConfig';
import styles from './StockManagement/StockManagementPage.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TableFilter from '../components/TableFilter';
import { useTheme } from '../contexts/ThemeContext';
import './StockManagement/StockManagementDarkMode.css';
import { Modal, Button, Form, Row, Col } from 'react-bootstrap';
import StockReportModal from './StockManagement/StockReportModal';
import * as XLSX from 'xlsx';

const truncateText = (text, maxLength = 15) => {
  if (
    text === null || 
    text === undefined || 
    text === '' || 
    Number.isNaN(text) ||
    (typeof text === 'string' && 
      ['nan', 'nat', 'n/a', 'null', 'undefined'].includes(text.toLowerCase()))
  ) {
    return '-';
  }
  const textStr = String(text);
  return textStr.length > maxLength 
    ? `${textStr.substring(0, maxLength)}...` 
    : textStr;
};

const StockManagementPage = () => {
  const { isDarkMode } = useTheme();
  const [stocks, setStocks] = useState([]);
  const [filteredStocks, setFilteredStocks] = useState([]);
  const [filters, setFilters] = useState({});
  const [selectedStock, setSelectedStock] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    'Adı': true,
    'Kategori': true,
    'Bilgi': true,
    'customerName': true,
    'projectName': true,
    'Depo': true,
    'Durum': true,
    'Döviz Kuru': false,
    'Döviz Tipi': true,
    'Giriş Fiyatı': true,
    'Kalan': true,
    'Min Miktar': true,
    'Kar Oranı(%)': false,
    'Kullanma Başlama Tarihi': true,
    'Miktar': true,
    'Son Kullanma Tarihi': true,
    'Stok Tipi': true,
    'Yıpranma Payı': false,
    'Çıkış Fiyatı': true
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [newStock, setNewStock] = useState({});
  const [showExpiringOnly, setShowExpiringOnly] = useState(false);
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editStock, setEditStock] = useState({});
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [activeEmployees, setActiveEmployees] = useState([]);
  const [movementData, setMovementData] = useState({
    employeeId: '',
    quantity: '',
    note: ''
  });
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [stockHistory, setStockHistory] = useState([]);
  const [stockTypes, setStockTypes] = useState([]);
  const [showStockTypeModal, setShowStockTypeModal] = useState(false);
  const [newStockType, setNewStockType] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState(null);

  // Assigned Stocks modal states
  const [showAssignedStocksModal, setShowAssignedStocksModal] = useState(false);
  const [assignedStocksData, setAssignedStocksData] = useState({ byEmployee: [], byStock: [] });
  const [assignedStocksLoading, setAssignedStocksLoading] = useState(false);
  const [assignedStocksSearch, setAssignedStocksSearch] = useState('');
  const [assignedStocksViewMode, setAssignedStocksViewMode] = useState('employee');
  // 'all' | 'assigned' | 'unassigned' — hiç atama yapılmamış çalışanları
  // ayıklamak için (varsayılan: hepsi görünür).
  const [assignedEmployeeFilter, setAssignedEmployeeFilter] = useState('all');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);

  // Card filter state: 'all', 'active', 'inactive', 'low'
  const [cardFilter, setCardFilter] = useState('all');

  // Sarf malzemelerinin bağlanabileceği müşteri ve projeler
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  // Kullanım raporu (kime ne kadar verildi / ne kadar kullanıldı)
  const [showReportModal, setShowReportModal] = useState(false);

  // Category filter state: 'all', 'sarf', 'demirbas'
  const [categoryFilter, setCategoryFilter] = useState('all');

  const tableContainerRef = useRef(null);
  const filtersRef = useRef(null);

  const columns = {
    'Adı': { label: 'Ürün Adı', type: 'text' },
    'Kategori': {
      label: 'Kategori',
      type: 'select',
      options: [
        { value: "", label: "Tümü" },
        { value: "sarf", label: "Sarf Malzemesi" },
        { value: "demirbas", label: "Demirbaş" }
      ]
    },
    'Bilgi': { label: 'Bilgi', type: 'text' },
    // Sarf malzemeleri bir müşteriye/projeye bağlanabilir; demirbaşta boş kalır
    'customerName': { label: 'Müşteri', type: 'text' },
    'projectName': { label: 'Proje', type: 'text' },
    'Depo': { label: 'Depo', type: 'text' },
    'Durum': {
      label: 'Durum',
      type: 'select',
      options: [
        { value: "", label: "Tümü" },
        { value: "aktif", label: "Aktif" },
        { value: "inaktif", label: "İnaktif" }
      ]
    },
    'Döviz Kuru': { label: 'Döviz Kuru', type: 'text' },
    'Döviz Tipi': {
      label: 'Döviz Tipi',
      type: 'select',
      options: [
        { value: "", label: "Tümü" },
        { value: "TRY", label: "TRY" },
        { value: "USD", label: "USD" },
        { value: "EUR", label: "EUR" }
      ]
    },
    'Giriş Fiyatı': { label: 'Giriş Fiyatı', type: 'text' },
    'Kalan': { label: 'Kalan', type: 'text' },
    'Min Miktar': { label: 'Min Miktar', type: 'text' },
    'Kar Oranı(%)': { label: 'Kar Oranı(%)', type: 'text' },
    'Kullanma Başlama Tarihi': { label: 'Kullanma Başlama Tarihi', type: 'date' },
    'Miktar': { label: 'Miktar', type: 'text' },
    'Son Kullanma Tarihi': { label: 'Son Kullanma Tarihi', type: 'date' },
    'Stok Tipi': { label: 'Stok Tipi', type: 'text' },
    'Yıpranma Payı': { label: 'Yıpranma Payı', type: 'text' },
    'Çıkış Fiyatı': { label: 'Çıkış Fiyatı', type: 'text' }
  };

  // Function to handle filter changes
  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };
  
  // Clear all filters
  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
    setShowExpiringOnly(false);
    setShowLowStockOnly(false);
  };

  // Toggle column visibility
  const toggleColumnVisibility = (column) => {
    setVisibleColumns(prev => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  // Handle stock selection
  const handleStockSelect = (stock) => {
    setSelectedStock(stock);
  };

  // Modal input değişikliği
  const handleNewStockChange = (key, value) => {
    let updated = { ...newStock, [key]: value };
    // Giriş Fiyatı veya Çıkış Fiyatı değişirse kar oranını hesapla
    if (key === 'Giriş Fiyatı' || key === 'Çıkış Fiyatı') {
      const giris = parseFloat(key === 'Giriş Fiyatı' ? value : updated['Giriş Fiyatı']);
      const cikis = parseFloat(key === 'Çıkış Fiyatı' ? value : updated['Çıkış Fiyatı']);
      if (!isNaN(giris) && !isNaN(cikis) && giris !== 0) {
        const oran = ((cikis - giris) / giris) * 100;
        updated['Kar Oranı(%)'] = oran.toFixed(2);
      } else {
        updated['Kar Oranı(%)'] = '';
      }
    }
    setNewStock(updated);
  };

  // Modal kaydet
  const handleAddStock = async () => {
    try {
      const stockToSave = { ...newStock };
      if (!stockToSave['Min Miktar'] && stockToSave['Min Miktar'] !== 0) {
        stockToSave['Min Miktar'] = '1';
      }
      await addDoc(collection(db, 'Stocks'), stockToSave);
      toast.success('Stok başarıyla eklendi!');
      setShowAddModal(false);
      setNewStock({});
      
      // Refresh both stocks and stock types
      await Promise.all([
        fetchStocks(),
        fetchStockTypes()
      ]);
    } catch (error) {
      toast.error('Stok eklenirken hata oluştu: ' + error.message);
    }
  };

  const handleCloseAddModal = () => {
    setShowAddModal(false);
    setNewStock({});
  };

  /**
   * Müşteri adlarını karşılaştırmaya hazırlar.
   *
   * Proje kayıtları müşteriyi ADIYLA taşır (Projects.customer), müşteri listesi
   * ise Customers.Başlık'tan gelir. İki metin çoğu zaman aynı değildir:
   * "ICON CLİNİCAL RESEARCH LİMİTED" ile "ICON CLINICAL RESEARCH LIMITED"
   * gibi Türkçe karakter farkları, fazladan boşluk veya büyük/küçük harf
   * farkı birebir karşılaştırmayı boşa çıkarıyordu. Aksanlar sadeleştirilip
   * boşluklar tekilleştirilerek karşılaştırma yapılır.
   */
  const normalizeCustomerName = (value) =>
    String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[ıI]/g, 'i')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();

  /**
   * Müşteri/proje seçimini tek yerden yönetir.
   *
   * - Müşteri değişince, o müşteriye ait olmayan proje seçimi düşürülür.
   * - Proje seçilince müşteri kendiliğinden dolar (proje zaten bir müşteriye
   *   ait), böylece iki alan hiçbir zaman çelişmez.
   */
  const applyScopeChange = (source, field, value) => {
    const updated = { ...source };

    if (field === 'customerName') {
      updated.customerName = value;
      const project = projects.find((p) => p.name === updated.projectName);
      const sameCustomer =
        project && normalizeCustomerName(project.customerName) === normalizeCustomerName(value);
      if (value && project && !sameCustomer) {
        updated.projectName = '';
        updated.projectId = '';
      }
      return updated;
    }

    updated.projectName = value;
    const project = projects.find((p) => p.name === value);
    updated.projectId = project?.id || '';
    if (project?.customerName) updated.customerName = project.customerName;
    return updated;
  };

  /**
   * Seçili müşterinin projeleri. Müşteri seçilmemişse tüm projeler listelenir.
   *
   * Eşleşen proje bulunamazsa liste boş bırakılmaz: müşteri adı proje kaydında
   * farklı yazılmış olabileceği için tüm projeler gösterilir ve proje seçimi
   * müşteri alanını kendiliğinden düzeltir. Böylece hatalı bir eşleşme
   * kullanıcının proje seçmesini engellemez.
   */
  const projectsForCustomer = (customerName) => {
    if (!customerName) return projects;
    const target = normalizeCustomerName(customerName);
    const matches = projects.filter(
      (p) => normalizeCustomerName(p.customerName) === target
    );
    return matches.length > 0 ? matches : projects;
  };

  // Seçili müşteriye bağlı proje bulunamadı mı? (uyarı metni için)
  const hasNoMatchingProject = (customerName) =>
    Boolean(customerName) &&
    projects.length > 0 &&
    !projects.some((p) => normalizeCustomerName(p.customerName) === normalizeCustomerName(customerName));

  // Edit modal açma
  const handleOpenEditModal = () => {
    if (!selectedStock) {
      toast.warning('Lütfen düzenlemek için bir stok seçin');
      return;
    }
    setEditStock({ ...selectedStock });
    setShowEditModal(true);
  };

  // Edit modal input değişikliği
  const handleEditStockChange = (key, value) => {
    let updated = { ...editStock, [key]: value };
    // Giriş Fiyatı veya Çıkış Fiyatı değişirse kar oranını hesapla
    if (key === 'Giriş Fiyatı' || key === 'Çıkış Fiyatı') {
      const giris = parseFloat(key === 'Giriş Fiyatı' ? value : updated['Giriş Fiyatı']);
      const cikis = parseFloat(key === 'Çıkış Fiyatı' ? value : updated['Çıkış Fiyatı']);
      if (!isNaN(giris) && !isNaN(cikis) && giris !== 0) {
        const oran = ((cikis - giris) / giris) * 100;
        updated['Kar Oranı(%)'] = oran.toFixed(2);
      } else {
        updated['Kar Oranı(%)'] = '';
      }
    }
    setEditStock(updated);
  };

  // Edit modal kaydet
  const handleUpdateStock = async () => {
    try {
      const stockRef = doc(db, 'Stocks', editStock.id);
      const { id, ...stockData } = editStock;
      await updateDoc(stockRef, stockData);
      toast.success('Stok başarıyla güncellendi!');
      setShowEditModal(false);
      setEditStock({});
      setSelectedStock(null);
      
      // Refresh both stocks and stock types
      await Promise.all([
        fetchStocks(),
        fetchStockTypes()
      ]);
    } catch (error) {
      toast.error('Stok güncellenirken hata oluştu: ' + error.message);
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditStock({});
  };

  // Durum değiştir (aktif/inaktif toggle)
  const handleToggleStatus = async () => {
    if (!selectedStock) {
      toast.warning('Lütfen durum değiştirmek için bir stok seçin');
      return;
    }

    try {
      const newStatus = selectedStock['Durum'] === 'aktif' ? 'inaktif' : 'aktif';
      const stockRef = doc(db, 'Stocks', selectedStock.id);
      await updateDoc(stockRef, { 'Durum': newStatus });
      toast.success(`Stok durumu ${newStatus} olarak güncellendi!`);
      setSelectedStock({ ...selectedStock, 'Durum': newStatus });
      
      // Refresh both stocks and stock types
      await Promise.all([
        fetchStocks(),
        fetchStockTypes()
      ]);
    } catch (error) {
      toast.error('Durum değiştirilirken hata oluştu: ' + error.message);
    }
  };

  // Kategori değiştir (sarf/demirbas toggle)
  const handleToggleCategory = async () => {
    if (!selectedStock) {
      toast.warning('Lütfen kategori değiştirmek için bir stok seçin');
      return;
    }

    try {
      const currentCategory = selectedStock['Kategori'];
      const newCategory = currentCategory === 'sarf' ? 'demirbas' : 'sarf';
      const stockRef = doc(db, 'Stocks', selectedStock.id);
      await updateDoc(stockRef, { 'Kategori': newCategory });
      toast.success(
        newCategory === 'sarf'
          ? 'Stok "Sarf Malzemesi" olarak güncellendi!'
          : 'Stok "Demirbaş" olarak güncellendi!'
      );
      setSelectedStock({ ...selectedStock, 'Kategori': newCategory });
      
      // Refresh both stocks and stock types
      await Promise.all([
        fetchStocks(),
        fetchStockTypes()
      ]);
    } catch (error) {
      toast.error('Kategori değiştirilirken hata oluştu: ' + error.message);
    }
  };

  // Fetch active employees
  const fetchActiveEmployees = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Employees'));
      const employeesData = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(emp => emp.Active === '1' || emp.Active === 1);
      setActiveEmployees(employeesData);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Çalışanlar yüklenirken hata oluştu');
    }
  };

  // Stok hareketi modal açma
  const handleOpenMovementModal = async () => {
    if (!selectedStock) {
      toast.warning('Lütfen stok hareketi için bir stok seçin');
      return;
    }
    await fetchActiveEmployees();
    setMovementData({ employeeId: '', quantity: '', note: '' });
    setShowMovementModal(true);
  };

  // Stok hareketi kaydet
  const handleSaveMovement = async () => {
    if (!movementData.employeeId) {
      toast.warning('Lütfen bir çalışan seçin');
      return;
    }
    if (!movementData.quantity || parseFloat(movementData.quantity) <= 0) {
      toast.warning('Lütfen geçerli bir miktar girin');
      return;
    }

    const requestedQty = parseFloat(movementData.quantity);
    const currentRemaining = parseFloat(selectedStock['Kalan']) || 0;

    if (requestedQty > currentRemaining) {
      toast.error(`Yetersiz stok! Kalan miktar: ${currentRemaining}`);
      return;
    }

    try {
      const newRemaining = currentRemaining - requestedQty;
      const stockRef = doc(db, 'Stocks', selectedStock.id);

      // Create movement record
      const selectedEmployee = activeEmployees.find(emp => emp.id === movementData.employeeId);
      const fullName = selectedEmployee ? `${selectedEmployee.eName || ''} ${selectedEmployee.eSurname || ''}`.trim() : '';
      const movementRecord = {
        stockId: selectedStock.id,
        stockName: selectedStock['Adı'],
        employeeId: movementData.employeeId,
        employeeName: fullName,
        quantity: requestedQty,
        note: movementData.note,
        date: new Date().toISOString(),
        type: 'assignment',
        // Stok kartındaki müşteri/proje bağı harekete kopyalanır; stok kartı
        // sonradan değişse bile geçmiş rapor doğru kalır.
        customerName: selectedStock.customerName || '',
        projectId: selectedStock.projectId || '',
        projectName: selectedStock.projectName || ''
      };

      // Save movement to StockMovements collection
      await addDoc(collection(db, 'StockMovements'), movementRecord);

      // Update stock remaining quantity
      await updateDoc(stockRef, { 'Kalan': newRemaining });

      toast.success(`${requestedQty} adet ${selectedStock['Adı']} ${fullName || ''} adlı çalışana atandı`);
      setShowMovementModal(false);
      setMovementData({ employeeId: '', quantity: '', note: '' });
      setSelectedStock({ ...selectedStock, 'Kalan': newRemaining });
      fetchStocks();
    } catch (error) {
      console.error('Error saving movement:', error);
      toast.error('Stok hareketi kaydedilirken hata oluştu: ' + error.message);
    }
  };

  const handleCloseMovementModal = () => {
    setShowMovementModal(false);
    setMovementData({ employeeId: '', quantity: '', note: '' });
  };

  // Fetch stock history
  const fetchStockHistory = async (stockId) => {
    try {
      const q = query(
        collection(db, 'StockMovements'),
        where('stockId', '==', stockId)
      );
      const querySnapshot = await getDocs(q);
      const history = querySnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => {
          // Sort by date descending (newest first)
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          return dateB - dateA;
        });

      // Resolve employee names from Employees and ExternalPersonnels collections
      const allEmployeeIds = [...new Set(
        history
          .filter(m => m.employeeId)
          .map(m => m.employeeId)
      )];

      if (allEmployeeIds.length > 0) {
        const empMap = {};
        await Promise.all(allEmployeeIds.map(async (empId) => {
          try {
            // Try Employees collection first
            const empDoc = await getDoc(doc(db, 'Employees', empId));
            if (empDoc.exists()) {
              const d = empDoc.data();
              const fullName = `${d.eName || ''} ${d.eSurname || ''}`.trim();
              if (fullName) {
                empMap[empId] = fullName;
                return;
              }
            }
            // Fallback to ExternalPersonnels collection
            const extDoc = await getDoc(doc(db, 'ExternalPersonnels', empId));
            if (extDoc.exists()) {
              const d = extDoc.data();
              const fullName = `${d['Adı'] || ''} ${d['Soyadı'] || ''}`.trim();
              if (fullName) {
                empMap[empId] = fullName;
              }
            }
          } catch (e) {
            console.error('Error fetching employee:', empId, e);
          }
        }));

        history.forEach(m => {
          if (m.employeeId && empMap[m.employeeId]) {
            m.employeeName = empMap[m.employeeId];
          }
        });
      }

      setStockHistory(history);
    } catch (error) {
      console.error('Error fetching stock history:', error);
      toast.error('Stok geçmişi yüklenirken hata oluştu');
    }
  };

  // Excel export function for stock history
  const handleExportStockHistory = () => {
    if (!stockHistory || stockHistory.length === 0) {
      toast.warning('Dışa aktarılacak stok hareketi bulunamadı');
      return;
    }

    try {
      // Prepare data for Excel
      const excelData = stockHistory.map((movement, index) => {
        const isVisitMovement = movement.type === 'visit_usage' || movement.type === 'visit_return';
        const isReturn = movement.type === 'visit_return' || movement.quantity < 0;
        const displayQuantity = Math.abs(movement.quantity);
        
        // employeeName zaten fetchStockHistory'de tam ad olarak set edilmiş olmalı
        const employeeName = movement.employeeName || 'Sistem';

        return {
          'Sıra No': index + 1,
          'Tarih': formatDateTime(movement.date),
          'Çalışan/Ziyaret': isVisitMovement 
            ? `${employeeName} (Ziyaret: ${movement.visitId || 'N/A'})`
            : employeeName,
          'Miktar': displayQuantity,
          'Tip': movement.type === 'assignment' ? 'Atama' :
                movement.type === 'visit_usage' ? 'Ziyaret Kullanımı' :
                movement.type === 'visit_return' ? 'Ziyaret İadesi' :
                movement.type === 'adjustment' ? 'Düzeltme' :
                movement.type === 'return' ? 'İade' :
                movement.type || 'Bilinmeyen',
          'İşlem': isReturn ? 'Giriş (+)' : 'Çıkış (-)',
          'Not': movement.note || '-'
        };
      });

      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      const colWidths = [
        { wch: 8 },  // Sıra No
        { wch: 18 }, // Tarih
        { wch: 30 }, // Çalışan/Ziyaret
        { wch: 10 }, // Miktar
        { wch: 15 }, // Tip
        { wch: 12 }, // İşlem
        { wch: 30 }  // Not
      ];
      ws['!cols'] = colWidths;

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Stok Hareketleri');

      // Generate filename
      const stockName = selectedStock?.['Adı'] || 'Stok';
      const currentDate = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
      const filename = `${stockName}_Stok_Hareketleri_${currentDate}.xlsx`;

      // Save file
      XLSX.writeFile(wb, filename);
      
      toast.success('Stok hareketleri Excel dosyası başarıyla indirildi!');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Excel dosyası oluşturulurken bir hata oluştu');
    }
  };

  // Function to export ALL stock movements across all stocks
  const handleExportAllStockMovements = async () => {
    try {
      // Check if there are any filtered stocks first
      if (filteredStocks.length === 0) {
        toast.warning('Seçili filtrelere uygun stok bulunamadı');
        return;
      }

      toast.info('Stok hareketleri yükleniyor...');

      // Fetch ALL stock movements
      const movementsSnapshot = await getDocs(collection(db, 'StockMovements'));
      const allMovements = movementsSnapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const dateA = new Date(a.date || 0);
          const dateB = new Date(b.date || 0);
          return dateB - dateA;
        });

      if (allMovements.length === 0) {
        toast.warning('Dışa aktarılacak stok hareketi bulunamadı');
        return;
      }

      // Use filteredStocks to get the IDs of stocks that match current filters
      const filteredStockIds = new Set(filteredStocks.map(s => s.id));

      // Filter movements to only include those for filtered stocks
      const filteredMovements = allMovements.filter(movement => 
        filteredStockIds.has(movement.stockId)
      );

      if (filteredMovements.length === 0) {
        toast.warning('Filtrelenmiş stoklar için hareket bulunamadı');
        return;
      }

      // Build a stockId -> stock name map from current stocks
      const stockNameMap = {};
      stocks.forEach(s => {
        stockNameMap[s.id] = s['Adı'] || 'Bilinmeyen';
      });

      // Resolve employee names
      const allEmployeeIds = [...new Set(
        filteredMovements.filter(m => m.employeeId).map(m => m.employeeId)
      )];

      const empMap = {};
      if (allEmployeeIds.length > 0) {
        await Promise.all(allEmployeeIds.map(async (empId) => {
          try {
            const empDoc = await getDoc(doc(db, 'Employees', empId));
            if (empDoc.exists()) {
              const d = empDoc.data();
              const fullName = `${d.eName || ''} ${d.eSurname || ''}`.trim();
              if (fullName) { empMap[empId] = fullName; return; }
            }
            const extDoc = await getDoc(doc(db, 'ExternalPersonnels', empId));
            if (extDoc.exists()) {
              const d = extDoc.data();
              const fullName = `${d['Adı'] || ''} ${d['Soyadı'] || ''}`.trim();
              if (fullName) { empMap[empId] = fullName; }
            }
          } catch (e) {
            console.error('Error fetching employee:', empId, e);
          }
        }));
      }

      // Prepare Excel data
      const excelData = filteredMovements.map((movement, index) => {
        const isVisitMovement = movement.type === 'visit_usage' || movement.type === 'visit_return';
        
        // Önce empMap'ten tam adı al, yoksa movement.employeeName kullan
        const employeeName = empMap[movement.employeeId] || movement.employeeName || 'Sistem';

        return {
          'Sıra No': index + 1,
          'Tarih': formatDateTime(movement.date),
          'Stok Adı': stockNameMap[movement.stockId] || movement.stockId || '-',
          'Çalışan/Ziyaret': isVisitMovement
            ? `${employeeName} (Ziyaret: ${movement.visitId || 'N/A'})`
            : employeeName,
          'Miktar': movement.quantity > 0 ? `+${movement.quantity}` : `${movement.quantity}`,
          'Tip': movement.type === 'assignment' ? 'Çalışan Ataması' :
                movement.type === 'visit_usage' ? 'Ziyaret Kullanımı' :
                movement.type === 'visit_return' ? 'Ziyaret İadesi' :
                movement.type === 'adjustment' ? 'Düzeltme' :
                movement.type === 'return' ? 'İade' :
                movement.type || 'Bilinmeyen',
          'Not': movement.note || '-'
        };
      });

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);

      ws['!cols'] = [
        { wch: 8 },  // Sıra No
        { wch: 18 }, // Tarih
        { wch: 30 }, // Stok Adı
        { wch: 35 }, // Çalışan/Ziyaret
        { wch: 10 }, // Miktar
        { wch: 20 }, // Tip
        { wch: 40 }  // Not
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Stok Hareketleri');

      const currentDate = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
      
      // Add filter info to filename if filters are active
      let filterSuffix = '';
      if (filteredMovements.length < allMovements.length) {
        filterSuffix = '_Filtreli';
      }
      
      const filename = `Stok_Hareketleri${filterSuffix}_${filteredMovements.length}_Kayit_${currentDate}.xlsx`;

      XLSX.writeFile(wb, filename);
      toast.success(`${filteredMovements.length} adet stok hareketi Excel dosyasına aktarıldı!`);
    } catch (error) {
      console.error('All stock movements Excel export error:', error);
      toast.error('Excel dosyası oluşturulurken bir hata oluştu');
    }
  };

  // Function to download all stocks with detailed information as Excel
  const downloadAllStocksExcel = async () => {
    try {
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Stoklar');

      // Define columns with more detailed information
      worksheet.columns = [
        { header: 'Sıra No', key: 'siraNo', width: 8 },
        { header: 'Ürün Adı', key: 'urunAdi', width: 35 },
        { header: 'Kategori', key: 'kategori', width: 15 },
        { header: 'Stok Tipi', key: 'stokTipi', width: 25 },
        { header: 'Bilgi/Açıklama', key: 'bilgi', width: 40 },
        { header: 'Depo', key: 'depo', width: 20 },
        { header: 'Durum', key: 'durum', width: 12 },
        { header: 'Toplam Miktar', key: 'miktar', width: 15 },
        { header: 'Kalan Miktar', key: 'kalan', width: 15 },
        { header: 'Min Miktar', key: 'minMiktar', width: 12 },
        { header: 'Giriş Fiyatı', key: 'girisFiyati', width: 15 },
        { header: 'Çıkış Fiyatı', key: 'cikisFiyati', width: 15 },
        { header: 'Döviz Tipi', key: 'dovizTipi', width: 12 },
        { header: 'Döviz Kuru', key: 'dovizKuru', width: 15 },
        { header: 'Kar Oranı (%)', key: 'karOrani', width: 15 },
        { header: 'Yıpranma Payı', key: 'yipranmaPayi', width: 15 },
        { header: 'Kullanma Başlama Tarihi', key: 'kullanmaBaslamaTarihi', width: 25 },
        { header: 'Son Kullanma Tarihi', key: 'sonKullanmaTarihi', width: 25 },
        { header: 'Stok Durumu', key: 'stokDurumu', width: 15 }
      ];

      // Check if there are any filtered stocks
      if (filteredStocks.length === 0) {
        toast.warning('Seçili filtrelere uygun stok bulunamadı');
        return;
      }

      // Add data rows with detailed information
      filteredStocks.forEach((stock, index) => {
        const kalan = parseFloat(stock['Kalan']) || 0;
        const minMiktar = parseFloat(stock['Min Miktar']) || 1;
        const sonKullanma = stock['Son Kullanma Tarihi'];
        
        // Determine stock status
        let stokDurumu = 'Normal';
        if (kalan <= 0) {
          stokDurumu = 'Tükendi';
        } else if (kalan <= minMiktar) {
          stokDurumu = 'Düşük Stok';
        } else if (sonKullanma) {
          const today = new Date();
          const expiryDate = new Date(sonKullanma);
          const daysUntilExpiry = Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24));
          if (daysUntilExpiry <= 30 && daysUntilExpiry > 0) {
            stokDurumu = 'Yakında Sona Erecek';
          } else if (daysUntilExpiry <= 0) {
            stokDurumu = 'Süresi Dolmuş';
          }
        }

        worksheet.addRow({
          siraNo: index + 1,
          urunAdi: stock['Adı'] || '-',
          kategori: stock['Kategori'] || '-',
          stokTipi: stock['Stok Tipi'] || '-',
          bilgi: stock['Bilgi'] || '-',
          depo: stock['Depo'] || '-',
          durum: stock['Durum'] || '-',
          miktar: stock['Miktar'] || '-',
          kalan: stock['Kalan'] || '-',
          minMiktar: stock['Min Miktar'] || '-',
          girisFiyati: stock['Giriş Fiyatı'] || '-',
          cikisFiyati: stock['Çıkış Fiyatı'] || '-',
          dovizTipi: stock['Döviz Tipi'] || '-',
          dovizKuru: stock['Döviz Kuru'] || '-',
          karOrani: stock['Kar Oranı(%)'] || '-',
          yipranmaPayi: stock['Yıpranma Payı'] || '-',
          kullanmaBaslamaTarihi: stock['Kullanma Başlama Tarihi'] || '-',
          sonKullanmaTarihi: stock['Son Kullanma Tarihi'] || '-',
          stokDurumu: stokDurumu
        });
      });

      // Style header row
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, size: 14, name: 'Calibri', color: { argb: 'FFFFFF' } };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '28a745' } // Green background
        };
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center',
          wrapText: true
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Style data rows with conditional formatting
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          const stokDurumu = row.getCell('stokDurumu').value;
          
          row.eachCell((cell) => {
            cell.font = { size: 12, name: 'Calibri' };
            cell.alignment = {
              vertical: 'middle',
              horizontal: 'left',
              wrapText: true
            };
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
            
            // Color coding based on stock status
            if (stokDurumu === 'Tükendi') {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFEBEE' } // Light red
              };
            } else if (stokDurumu === 'Düşük Stok') {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFF3E0' } // Light orange
              };
            } else if (stokDurumu === 'Yakında Sona Erecek') {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFFDE7' } // Light yellow
              };
            } else if (stokDurumu === 'Süresi Dolmuş') {
              cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FFCDD2' } // Red
              };
            }
          });
        }
      });

      // Add auto filter
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: worksheet.columns.length }
      };

      // Freeze first row
      worksheet.views = [
        { state: 'frozen', ySplit: 1 }
      ];

      // Generate filename with current date and stock count
      const date = new Date();
      const formattedDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
      
      // Add filter info to filename if filters are active
      let filterSuffix = '';
      if (filteredStocks.length < stocks.length) {
        filterSuffix = '_Filtreli';
      }
      
      const filename = `Stoklar${filterSuffix}_${filteredStocks.length}_Adet_${formattedDate}.xlsx`;
      
      // Create and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`${filteredStocks.length} adet stok bilgisi Excel dosyasına aktarıldı!`);
    } catch (error) {
      console.error('Error downloading all stocks Excel file:', error);
      toast.error('Excel dosyası oluşturulurken bir hata oluştu');
    }
  };

  // Open history modal
  const handleOpenHistoryModal = async () => {
    if (!selectedStock) {
      toast.warning('Lütfen geçmiş görüntülemek için bir stok seçin');
      return;
    }
    await fetchStockHistory(selectedStock.id);
    setShowHistoryModal(true);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setStockHistory([]);
  };

  // Handle undo/return stock movement
  const handleUndoMovement = async (movement) => {
    if (!window.confirm(`${movement.quantity} adet ${selectedStock['Adı']} stoğu geri alınacak. Onaylıyor musunuz?`)) {
      return;
    }

    try {
      // Update stock quantity (add back the returned quantity)
      const stockRef = doc(db, 'Stocks', selectedStock.id);
      const currentRemaining = parseFloat(selectedStock['Kalan']) || 0;
      const newRemaining = currentRemaining + parseFloat(movement.quantity);

      await updateDoc(stockRef, { 'Kalan': newRemaining });

      // Delete the movement record
      const movementRef = doc(db, 'StockMovements', movement.id);
      await deleteDoc(movementRef);

      toast.success(`${movement.quantity} adet stok başarıyla geri alındı`);

      // Update selected stock state
      setSelectedStock({ ...selectedStock, 'Kalan': newRemaining });

      // Refresh stock history
      await fetchStockHistory(selectedStock.id);

      // Refresh stocks list
      fetchStocks();
    } catch (error) {
      console.error('Error undoing movement:', error);
      toast.error('Stok geri alınırken hata oluştu: ' + error.message);
    }
  };

  // Fetch stock types from Firestore
  const fetchStockTypes = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'StockTypes'));
      const typesData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Remove duplicates - keep only the first occurrence of each unique name
      const uniqueTypes = [];
      const seenNames = new Set();
      const duplicatesToDelete = [];
      
      for (const type of typesData) {
        const normalizedName = type.name.toLocaleLowerCase('tr-TR').trim();
        if (!seenNames.has(normalizedName)) {
          seenNames.add(normalizedName);
          uniqueTypes.push(type);
        } else {
          // Mark duplicate for deletion
          duplicatesToDelete.push(type.id);
        }
      }
      
      // Auto-delete duplicates from database
      if (duplicatesToDelete.length > 0) {
        console.log('Deleting duplicate stock types:', duplicatesToDelete);
        const deletePromises = duplicatesToDelete.map(id => 
          deleteDoc(doc(db, 'StockTypes', id))
        );
        await Promise.all(deletePromises);
        toast.info(`${duplicatesToDelete.length} tekrarlayan stok tipi temizlendi`);
      }
      
      console.log('Fetched stock types:', uniqueTypes);
      setStockTypes(uniqueTypes);
      
      // If no stock types exist, add default ones
      if (uniqueTypes.length === 0) {
        console.warn('No stock types found in database. Adding default stock types...');
        await addDefaultStockTypes();
      }
    } catch (error) {
      console.error('Error fetching stock types:', error);
      toast.error('Stok tipleri yüklenirken hata oluştu');
    }
  };

  // Add default stock types
  const addDefaultStockTypes = async () => {
    const defaultTypes = [
      'Tıbbi Malzeme',
      'Laboratuvar Malzemesi',
      'Ofis Malzemesi',
      'Elektronik',
      'Temizlik Malzemesi',
      'Kırtasiye',
      'İlaç',
      'Medikal Cihaz'
    ];

    try {
      const addPromises = defaultTypes.map(typeName => 
        addDoc(collection(db, 'StockTypes'), { name: typeName })
      );
      
      await Promise.all(addPromises);
      console.log('Default stock types added successfully');
      toast.success('Varsayılan stok tipleri eklendi!');
      
      // Refresh the stock types list
      await fetchStockTypes();
    } catch (error) {
      console.error('Error adding default stock types:', error);
      toast.error('Varsayılan stok tipleri eklenirken hata oluştu');
    }
  };

  // Add new stock type
  const handleAddStockType = async () => {
    if (!newStockType.trim()) {
      toast.warning('Lütfen bir stok tipi adı girin');
      return;
    }

    // Check if stock type already exists
    const exists = stockTypes.some(
      type => type.name.toLocaleLowerCase('tr-TR') === newStockType.trim().toLocaleLowerCase('tr-TR')
    );
    if (exists) {
      toast.warning('Bu stok tipi zaten mevcut');
      return;
    }

    try {
      await addDoc(collection(db, 'StockTypes'), { name: newStockType.trim() });
      toast.success('Stok tipi başarıyla eklendi!');
      setNewStockType('');
      setShowStockTypeModal(false);
      fetchStockTypes();
    } catch (error) {
      toast.error('Stok tipi eklenirken hata oluştu: ' + error.message);
    }
  };

  const handleCloseStockTypeModal = () => {
    setShowStockTypeModal(false);
    setNewStockType('');
  };

  // ─── Assigned Stocks ───────────────────────────────────────────────────────

  const getAvatarColor = (name) => {
    const palette = ['#6366f1', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308', '#22c55e', '#06b6d4', '#3b82f6', '#14b8a6'];
    return palette[(name || '').charCodeAt(0) % palette.length];
  };

  /**
   * Sarf malzemesini müşteri/projeye bağlamak için gereken listeler.
   * Proje kaydı müşteriyi adıyla taşıdığı için (Projects.customer), proje
   * seçimi müşteriye göre daraltılabiliyor.
   */
  const fetchCustomersAndProjects = async () => {
    try {
      const [customerSnap, projectSnap] = await Promise.all([
        getDocs(collection(db, 'Customers')),
        getDocs(collection(db, 'Projects')),
      ]);

      // Adlar her zaman metin değil: tamamen rakamdan oluşan müşteri/proje
      // adları Firestore'a sayı olarak yazılmış olabiliyor. String'e
      // çevirmeden sıralamak localeCompare'i patlatıyordu.
      setCustomers(
        customerSnap.docs
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              name: String(data.Başlık ?? data['Müşteri Adı'] ?? '').trim(),
            };
          })
          .filter((customer) => customer.name)
          .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
      );

      setProjects(
        projectSnap.docs
          .map((d) => {
            const data = d.data();
            return {
              id: d.id,
              name: String(data.titleShort ?? '').trim(),
              customerName: String(data.customer ?? '').trim(),
              // isTestProject bazı kayıtlarda 'true' metni olarak saklanıyor
              isTestProject: data.isTestProject === true || data.isTestProject === 'true',
            };
          })
          .filter((project) => project.name && !project.isTestProject)
          .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
      );
    } catch (error) {
      console.error('Müşteri/proje listesi alınamadı:', error);
    }
  };

  const fetchAssignedStocks = async () => {
    setAssignedStocksLoading(true);
    try {
      const [snapshot, employeesSnapshot] = await Promise.all([
        getDocs(collection(db, 'StockMovements')),
        getDocs(collection(db, 'Employees'))
      ]);
      const movements = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(m => m.type === 'assignment');

      // Group by employee
      const empMap = {};

      // Hiç ataması olmayan çalışanlar da listede görünsün diye haritayı önce
      // çalışan listesiyle dolduruyoruz (atama modalindaki ile aynı filtre).
      // Ataması olup bu listede olmayan kişiler (pasif/silinmiş çalışanlar)
      // aşağıdaki hareket döngüsünde ekleniyor; böylece iki küme birleşiyor ve
      // mevcut kayıtların hiçbiri kaybolmuyor.
      employeesSnapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(emp => emp.Active === '1' || emp.Active === 1)
        .forEach(emp => {
          empMap[emp.id] = {
            employeeId: emp.id,
            employeeName: `${emp.eName || ''} ${emp.eSurname || ''}`.trim() || 'Bilinmeyen',
            assignments: []
          };
        });

      movements.forEach(m => {
        const key = m.employeeId || 'unknown';
        if (!empMap[key]) {
          empMap[key] = {
            employeeId: key,
            employeeName: m.employeeName || 'Bilinmeyen',
            assignments: []
          };
        }
        empMap[key].assignments.push({
          stockId: m.stockId,
          stockName: m.stockName || '-',
          quantity: m.quantity,
          date: m.date,
          note: m.note,
          id: m.id
        });
      });

      // Group by stock
      const stockMap = {};
      movements.forEach(m => {
        const key = m.stockId || 'unknown';
        if (!stockMap[key]) {
          stockMap[key] = {
            stockId: key,
            stockName: m.stockName || '-',
            assignments: []
          };
        }
        stockMap[key].assignments.push({
          employeeId: m.employeeId,
          employeeName: m.employeeName || 'Bilinmeyen',
          quantity: m.quantity,
          date: m.date,
          note: m.note,
          id: m.id
        });
      });

      setAssignedStocksData({
        // Ataması olanlar önce gelir, atama yapılmamış çalışanlar listenin
        // sonunda toplanır; her iki küme de kendi içinde alfabetik.
        byEmployee: Object.values(empMap).sort((a, b) => {
          const aHas = a.assignments.length > 0;
          const bHas = b.assignments.length > 0;
          if (aHas !== bHas) return aHas ? -1 : 1;
          return a.employeeName.localeCompare(b.employeeName, 'tr');
        }),
        byStock: Object.values(stockMap).sort((a, b) =>
          a.stockName.localeCompare(b.stockName, 'tr')
        )
      });
    } catch (error) {
      toast.error('Atanan stoklar yüklenirken hata oluştu');
    } finally {
      setAssignedStocksLoading(false);
    }
  };

  const handleOpenAssignedStocksModal = async () => {
    setAssignedStocksSearch('');
    setAssignedStocksViewMode('employee');
    setAssignedEmployeeFilter('all');
    setShowAssignedStocksModal(true);
    await fetchAssignedStocks();
  };

  // Delete stock type
  const handleDeleteStockType = async (e, typeId, typeName) => {
    e.stopPropagation(); // Prevent card click

    const stockCount = stocks.filter(s => s['Stok Tipi'] === typeName).length;
    if (stockCount > 0) {
      toast.warning('Bu stok tipine ait ürünler var, silinemez!');
      return;
    }

    if (!window.confirm(`"${typeName}" stok tipini silmek istediğinize emin misiniz?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'StockTypes', typeId));
      toast.success('Stok tipi başarıyla silindi!');
      if (selectedTypeFilter === typeName) {
        setSelectedTypeFilter(null);
      }
      fetchStockTypes();
    } catch (error) {
      toast.error('Stok tipi silinirken hata oluştu: ' + error.message);
    }
  };

  // Format date for display
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('tr-TR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  // Input tipi belirleme fonksiyonu
  const getInputType = (key, col) => {
    const lower = key.toLowerCase() + ' ' + (col.label || '').toLowerCase();
    if (lower.includes('tarih')) return 'date';
    if (lower.includes('miktar') || lower.includes('fiyat') || lower.includes('kuru') || lower.includes('oran') || lower.includes('kalan') || lower.includes('pay')) return 'number';
    return 'text';
  };

  // Fetch stocks from Firestore
  const fetchStocks = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Stocks'));
      const stocksData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setStocks(stocksData);
    } catch (error) {
      console.error('Error fetching stocks:', error);
      toast.error('Stok verileri yüklenirken hata oluştu');
    }
  };

  // Apply filters to stocks
  useEffect(() => {
    let filtered = stocks;

    // Apply category filter first (sarf/demirbas)
    if (categoryFilter === 'sarf') {
      filtered = filtered.filter(stock => stock['Kategori'] === 'sarf');
    } else if (categoryFilter === 'demirbas') {
      filtered = filtered.filter(stock => stock['Kategori'] === 'demirbas');
    }

    // Apply card filter
    if (cardFilter === 'active') {
      filtered = filtered.filter(stock => stock['Durum'] === 'aktif');
    } else if (cardFilter === 'inactive') {
      filtered = filtered.filter(stock => stock['Durum'] === 'inaktif');
    } else if (cardFilter === 'low') {
      filtered = filtered.filter(stock => isLowStock(stock));
    }

    // Apply stock type filter
    if (selectedTypeFilter) {
      filtered = filtered.filter(stock => stock['Stok Tipi'] === selectedTypeFilter);
    }

    // Apply search term (searches name and info)
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLocaleLowerCase('tr-TR');
      filtered = filtered.filter(
        stock =>
          (stock['Adı'] && stock['Adı'].toString().toLocaleLowerCase('tr-TR').includes(lowercasedSearch)) ||
          (stock['Bilgi'] && stock['Bilgi'].toString().toLocaleLowerCase('tr-TR').includes(lowercasedSearch)) ||
          (stock['Stok Tipi'] && stock['Stok Tipi'].toString().toLocaleLowerCase('tr-TR').includes(lowercasedSearch))
      );
    }

    // Apply expiring filter if enabled
    if (showExpiringOnly) {
      const today = new Date();
      const oneMonthFromNow = new Date();
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

      filtered = filtered.filter(stock => {
        if (!stock['Son Kullanma Tarihi']) return false;

        const expiryDate = new Date(stock['Son Kullanma Tarihi']);
        return !isNaN(expiryDate) &&
               expiryDate <= oneMonthFromNow &&
               expiryDate >= today;
      });
    }

    // Apply low stock filter if enabled
    if (showLowStockOnly) {
      filtered = filtered.filter(stock => isLowStock(stock));
    }

    // Then apply individual column filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        if (key === 'Durum') {
          filtered = filtered.filter(stock => stock[key] === filters[key]);
        } else if (key === 'Kullanma Başlama Tarihi' || key === 'Son Kullanma Tarihi') {
          filtered = filtered.filter(stock =>
            stock[key]?.includes(filters[key])
          );
        } else {
          filtered = filtered.filter(stock =>
            String(stock[key])
              ?.toLocaleLowerCase('tr-TR')
              .includes(filters[key].toLocaleLowerCase('tr-TR'))
          );
        }
      }
    });

    setFilteredStocks(filtered);
  }, [stocks, filters, searchTerm, showExpiringOnly, showLowStockOnly, cardFilter, selectedTypeFilter, categoryFilter]);

  // Initial data fetch
  useEffect(() => {
    fetchStocks();
    fetchStockTypes();
    fetchCustomersAndProjects();

    // Add click event listener to close filters dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Düşük stok uyarısı kontrolü
  useEffect(() => {
    if (stocks.length > 0) {
      const lowStockProducts = stocks.filter(stock => isLowStock(stock));
      
      if (lowStockProducts.length > 0) {
        toast.warn(
          `⚠️ ${lowStockProducts.length} ürünün stok miktarı belirlenen minimum değerin altına düştü!`, 
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );
      }
    }
  }, [stocks]);

  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  // Handle table scrolling
  const handleScroll = (e) => {
    e.stopPropagation();
    
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + 
        (direction === 'right' ? scrollAmount : -scrollAmount);
      
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  // Setup scroll detection
  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll({ stopPropagation: () => {} }); // Initial check
      
      const preventWheelPropagation = (e) => {
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } = container;
        const hasVerticalScroll = scrollHeight > clientHeight;
        const hasHorizontalScroll = scrollWidth > clientWidth;

        if (hasVerticalScroll || hasHorizontalScroll) {
          e.stopPropagation();
          e.preventDefault();

          const scrollAmount = e.deltaY || e.deltaX;
          // Only scroll horizontally if shift is pressed
          if (e.shiftKey && hasHorizontalScroll) {
            container.scrollLeft += scrollAmount;
          } else if (hasVerticalScroll) {
            // Default to vertical scroll
            container.scrollTop += scrollAmount;
          } else if (hasHorizontalScroll) {
            // Only use horizontal if no vertical scroll available
            container.scrollLeft += scrollAmount;
          }
        }
      };
      
      container.addEventListener('scroll', handleScroll);
      container.addEventListener('wheel', preventWheelPropagation, { passive: false });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('wheel', preventWheelPropagation);
      };
    }
  }, [stocks, filteredStocks]);

  // Function to download table as Excel
  const downloadExcel = async () => {
    try {
      const ExcelJS = (await import('exceljs')).default;
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Stok');

      // Define columns
      worksheet.columns = [
        { header: 'Ürün Adı', key: 'urunAdi', width: 30 },
        { header: 'Bilgi', key: 'bilgi', width: 30 },
        { header: 'Depo', key: 'depo', width: 15 },
        { header: 'Durum', key: 'durum', width: 15 },
        { header: 'Döviz Kuru', key: 'dovizKuru', width: 15 },
        { header: 'Döviz Tipi', key: 'dovizTipi', width: 15 },
        { header: 'Giriş Fiyatı', key: 'girisFiyati', width: 15 },
        { header: 'Kalan', key: 'kalan', width: 10 },
        { header: 'Min Miktar', key: 'minMiktar', width: 12 },
        { header: 'Kar Oranı(%)', key: 'karOrani', width: 15 },
        { header: 'Kullanma Başlama Tarihi', key: 'kullanmaBaslamaTarihi', width: 25 },
        { header: 'Miktar', key: 'miktar', width: 10 },
        { header: 'Son Kullanma Tarihi', key: 'sonKullanmaTarihi', width: 25 },
        { header: 'Stok Tipi', key: 'stokTipi', width: 20 },
        { header: 'Yıpranma Payı', key: 'yipranmaPayi', width: 15 },
        { header: 'Çıkış Fiyatı', key: 'cikisFiyati', width: 15 }
      ];

      // Use filteredStocks - if empty, show warning and don't export
      if (filteredStocks.length === 0) {
        toast.warning('Seçili filtrelere uygun stok bulunamadı');
        return;
      }

      // Add data rows
      filteredStocks.forEach(stock => {
        worksheet.addRow({
          urunAdi: stock['Adı'] || '',
          bilgi: stock['Bilgi'] || '',
          depo: stock['Depo'] || '',
          durum: stock['Durum'] || '',
          dovizKuru: stock['Döviz Kuru'] || '',
          dovizTipi: stock['Döviz Tipi'] || '',
          girisFiyati: stock['Giriş Fiyatı'] || '',
          kalan: stock['Kalan'] || '',
          minMiktar: stock['Min Miktar'] ?? 1,
          karOrani: stock['Kar Oranı(%)'] || '',
          kullanmaBaslamaTarihi: stock['Kullanma Başlama Tarihi'] || '',
          miktar: stock['Miktar'] || '',
          sonKullanmaTarihi: stock['Son Kullanma Tarihi'] || '',
          stokTipi: stock['Stok Tipi'] || '',
          yipranmaPayi: stock['Yıpranma Payı'] || '',
          cikisFiyati: stock['Çıkış Fiyatı'] || ''
        });
      });

      // Style header row
      worksheet.getRow(1).eachCell((cell) => {
        cell.font = { bold: true, size: 16, name: 'Calibri' };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '92D050' }
        };
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'center',
          wrapText: true
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Style data rows
      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber > 1) {
          row.eachCell((cell) => {
            cell.font = { size: 13, name: 'Calibri' };
            cell.alignment = {
              vertical: 'middle',
              horizontal: 'left',
              wrapText: true
            };
            cell.border = {
              top: { style: 'thin' },
              left: { style: 'thin' },
              bottom: { style: 'thin' },
              right: { style: 'thin' }
            };
          });
        }
      });

      // Add auto filter
      worksheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: worksheet.columns.length }
      };

      // Generate filename with current date
      const date = new Date();
      const formattedDate = `${date.getDate().toString().padStart(2, '0')}.${(date.getMonth() + 1).toString().padStart(2, '0')}.${date.getFullYear()}`;
      
      // Add filter info to filename if filters are active
      let filterSuffix = '';
      if (filteredStocks.length < stocks.length) {
        filterSuffix = `_Filtreli_${filteredStocks.length}`;
      }
      
      // Create and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Stok${filterSuffix}_${formattedDate}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Excel dosyası başarıyla indirildi (${filteredStocks.length} kayıt)`);
    } catch (error) {
      console.error('Error downloading Excel file:', error);
      toast.error('Excel dosyası indirilirken hata oluştu');
    }
  };

  // Apply dark mode class to document body
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  // Number input scroll engellemesi
  useEffect(() => {
    const handleWheel = (e) => {
      // Eğer aktif element bir number input ise scroll'u engelle
      if (document.activeElement.type === 'number') {
        e.preventDefault();
      }
    };

    const handleKeyDown = (e) => {
      // Eğer aktif element bir number input ise arrow key'leri engelle
      if (document.activeElement.type === 'number' && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
        e.preventDefault();
      }
    };

    // Event listener'ları ekle
    document.addEventListener('wheel', handleWheel, { passive: false });
    document.addEventListener('keydown', handleKeyDown);

    // Cleanup
    return () => {
      document.removeEventListener('wheel', handleWheel);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Düşük stok kontrolü fonksiyonu
  const isLowStock = (stock) => {
    const kalan = Number(stock['Kalan']) || 0;
    const minMiktar = Number(stock['Min Miktar']);
    const threshold = isNaN(minMiktar) ? 1 : minMiktar;
    return kalan <= threshold;
  };

  // Get category-filtered stocks for stats
  const getCategoryFilteredStocks = () => {
    if (categoryFilter === 'sarf') {
      return stocks.filter(stock => stock['Kategori'] === 'sarf');
    } else if (categoryFilter === 'demirbas') {
      return stocks.filter(stock => stock['Kategori'] === 'demirbas');
    }
    return stocks;
  };

  const categoryStocks = getCategoryFilteredStocks();

  // Get stats for dashboard cards (based on category filter)
  const sarfCount = stocks.filter(stock => stock['Kategori'] === 'sarf').length;
  const demirbasCount = stocks.filter(stock => stock['Kategori'] === 'demirbas').length;
  const activeStocks = categoryStocks.filter(stock => stock['Durum'] === 'aktif').length;
  const inactiveStocks = categoryStocks.filter(stock => stock['Durum'] === 'inaktif').length;
  const lowStockItems = categoryStocks.filter(stock => isLowStock(stock)).length;

  // Pagination calculations
  const totalPages = Math.ceil(filteredStocks.length / pageSize);
  const paginatedStocks = filteredStocks.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm, showExpiringOnly, showLowStockOnly, cardFilter, selectedTypeFilter, categoryFilter]);

  // Pagination handlers
  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FaBoxOpen className={styles.headerIcon} />
              Stok Yönetimi
            </h1>
            <div className={styles.headerButtons}>
              <button
                className={styles.assignedStocksBtn}
                onClick={() => setShowReportModal(true)}
                style={{ backgroundColor: '#0f766e' }}
              >
                <FaChartBar className={styles.buttonIcon} /> Kullanım Raporu
              </button>
              <button className={styles.assignedStocksBtn} onClick={handleOpenAssignedStocksModal}>
                <FaUsers className={styles.buttonIcon} /> Atanan Stoklar
              </button>
              <button className={styles.addButton} onClick={() => setShowAddModal(true)}>
                <FaPlus className={styles.buttonIcon} /> Yeni Stok Ekle
              </button>
              <button className={styles.addButton} onClick={() => setShowStockTypeModal(true)}>
                <FaTags className={styles.buttonIcon} /> Stok Tipi Ekle
              </button>
              <button className={styles.downloadButton} onClick={handleExportAllStockMovements}>
                <FaHistory className={styles.buttonIcon} /> Stok Hareketleri Excel
              </button>
              <button className={styles.downloadButton} onClick={downloadAllStocksExcel} style={{ backgroundColor: '#28a745' }}>
                <FaDownload className={styles.buttonIcon} /> Tüm Stoklar Excel
              </button>
            </div>
          </div>
        </header>

        {/* Category Tabs - Sarf / Demirbaş */}
        <div className={styles.categoryTabsContainer}>
          <div className={styles.categoryTabs}>
            <button
              className={`${styles.categoryTab} ${categoryFilter === 'all' ? styles.categoryTabActive : ''}`}
              onClick={() => setCategoryFilter('all')}
            >
              <FaBoxOpen className={styles.categoryTabIcon} />
              <span className={styles.categoryTabLabel}>Tüm Stoklar</span>
              <span className={styles.categoryTabCount}>{stocks.length}</span>
            </button>
            <button
              className={`${styles.categoryTab} ${styles.sarfTab} ${categoryFilter === 'sarf' ? styles.categoryTabActive : ''}`}
              onClick={() => setCategoryFilter('sarf')}
            >
              <FaCubes className={styles.categoryTabIcon} />
              <span className={styles.categoryTabLabel}>Sarf Malzemesi</span>
              <span className={styles.categoryTabCount}>{sarfCount}</span>
            </button>
            <button
              className={`${styles.categoryTab} ${styles.demirbasTab} ${categoryFilter === 'demirbas' ? styles.categoryTabActive : ''}`}
              onClick={() => setCategoryFilter('demirbas')}
            >
              <FaTools className={styles.categoryTabIcon} />
              <span className={styles.categoryTabLabel}>Demirbaş</span>
              <span className={styles.categoryTabCount}>{demirbasCount}</span>
            </button>
          </div>
          {categoryFilter !== 'all' && (
            <div className={styles.categoryInfo}>
              {categoryFilter === 'sarf' ? (
                <p>Sarf malzemeleri: Tüketilen, son kullanma tarihli ürünler (tıbbi malzeme, temizlik vb.)</p>
              ) : (
                <p>Demirbaş: Uzun ömürlü, amortisman hesaplanan varlıklar (ekipman, mobilya vb.)</p>
              )}
            </div>
          )}
        </div>

        <div className={`${styles.dashboard} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.statsCards}>
            <div
              className={`${styles.card} ${styles.clickableCard} ${cardFilter === 'all' ? styles.selectedCard : ''}`}
              onClick={() => setCardFilter('all')}
            >
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaBoxOpen />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{categoryStocks.length}</p>
                  <h3 className={styles.cardTitle}>
                    {categoryFilter === 'sarf' ? 'Sarf Malzemesi' : categoryFilter === 'demirbas' ? 'Demirbaş' : 'Toplam Stok'}
                  </h3>
                </div>
              </div>
            </div>

            <div
              className={`${styles.card} ${styles.activeCard} ${styles.clickableCard} ${cardFilter === 'active' ? styles.selectedCard : ''}`}
              onClick={() => setCardFilter('active')}
            >
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaBoxOpen />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{activeStocks}</p>
                  <h3 className={styles.cardTitle}>Aktif Stok</h3>
                </div>
              </div>
            </div>

            <div
              className={`${styles.card} ${styles.inactiveCard} ${styles.clickableCard} ${cardFilter === 'inactive' ? styles.selectedCard : ''}`}
              onClick={() => setCardFilter('inactive')}
            >
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaBoxOpen />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{inactiveStocks}</p>
                  <h3 className={styles.cardTitle}>İnaktif Stok</h3>
                </div>
              </div>
            </div>

            <div
              className={`${styles.card} ${styles.warningCard} ${styles.clickableCard} ${cardFilter === 'low' ? styles.selectedCard : ''}`}
              onClick={() => setCardFilter('low')}
            >
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaBoxOpen />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{lowStockItems}</p>
                  <h3 className={styles.cardTitle}>Azalan Stok</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Stock Type Filter Cards */}
          {stockTypes.length > 0 && (
            <div className={styles.stockTypesContainer}>
              <div className={styles.stockTypesCompact}>
                <div className={styles.stockTypesLabel}>
                  <FaTags className={styles.labelIcon} />
                  <span>Stok Tipi Filtrele:</span>
                </div>
                <select
                  className={styles.stockTypeSelect}
                  value={selectedTypeFilter || ''}
                  onChange={(e) => setSelectedTypeFilter(e.target.value || null)}
                >
                  <option value="">Tüm Tipler ({stocks.length})</option>
                  {stockTypes.map(type => {
                    const count = stocks.filter(s => s['Stok Tipi'] === type.name).length;
                    return (
                      <option key={type.id} value={type.name}>
                        {type.name} ({count})
                      </option>
                    );
                  })}
                </select>
                {selectedTypeFilter && (
                  <button
                    className={styles.clearFilterButton}
                    onClick={() => setSelectedTypeFilter(null)}
                    title="Filtreyi Temizle"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            </div>
          )}

          <div className={styles.expiringFilterContainer}>
            <div className={styles.expiringCheckbox}>
              <input
                type="checkbox"
                id="showExpiringOnly"
                checked={showExpiringOnly}
                onChange={(e) => setShowExpiringOnly(e.target.checked)}
              />
              <FaExclamationTriangle className={styles.warningIcon} />
              <label htmlFor="showExpiringOnly">
                Son Kullanım Tarihi Yaklaşanları Listele
              </label>
            </div>
          </div>

          <div className={styles.expiringFilterContainer}>
            <div className={styles.expiringCheckbox}>
              <input
                type="checkbox"
                id="showLowStockOnly"
                checked={showLowStockOnly}
                onChange={(e) => setShowLowStockOnly(e.target.checked)}
              />
              <FaExclamationTriangle className={styles.warningIcon} style={{ color: '#ef4444' }} />
              <label htmlFor="showLowStockOnly">
                Düşük Stok Ürünlerini Listele (Min Miktar altı)
              </label>
            </div>
          </div>

          <div className={styles.searchFilterContainer}>
            <div className={styles.searchBar}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Ürün adı, bilgi veya stok tipi ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button 
                  className={styles.clearSearch} 
                  onClick={() => setSearchTerm('')}
                >
                  <FaTimes />
                </button>
              )}
            </div>
            
            <div className={styles.filterContainer} ref={filtersRef}>
              <button 
                className={styles.filterButton}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter className={styles.filterIcon} />
                <span>Filtreler</span>
                {Object.keys(filters).length > 0 && (
                  <span className={styles.filterBadge}>
                    {Object.keys(filters).length}
                  </span>
                )}
              </button>
              
              {showFilters && (
                <div className={styles.filterDropdown}>
                  <div className={styles.filterHeader}>
                    <h3>Filtreler</h3>
                    <button 
                      className={styles.clearFiltersBtn}
                      onClick={clearFilters}
                    >
                      Tümünü Temizle
                    </button>
                  </div>
                  
                  
                  <div className={styles.columnVisibility}>
                    <h4>Sütun Görünürlüğü</h4>
                    <div className={styles.columnCheckboxes}>
                      {Object.keys(columns).map(colKey => (
                        <div key={colKey} className={styles.columnCheckbox}>
                          <input
                            type="checkbox"
                            id={`col-${colKey}`}
                            checked={visibleColumns[colKey]}
                            onChange={() => toggleColumnVisibility(colKey)}
                          />
                          <label htmlFor={`col-${colKey}`}>
                            {columns[colKey].label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedStock && (
          <div className={`${styles.actionPanel} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.selectedStockInfo}>
              <h3 className={styles.selectedName}>
                {selectedStock['Adı']}
              </h3>
              <span className={`${styles.categoryBadge} ${
                selectedStock['Kategori'] === 'sarf'
                  ? styles.categorySarf
                  : selectedStock['Kategori'] === 'demirbas'
                    ? styles.categoryDemirbas
                    : styles.categoryUnknown
              }`}>
                {selectedStock['Kategori'] === 'sarf' ? (
                  <><FaCubes style={{ marginRight: '4px' }} /> Sarf</>
                ) : selectedStock['Kategori'] === 'demirbas' ? (
                  <><FaTools style={{ marginRight: '4px' }} /> Demirbaş</>
                ) : 'Belirsiz'}
              </span>
              <span className={`${styles.statusBadge} ${
                selectedStock['Durum'] === 'aktif'
                  ? styles.statusActive
                  : styles.statusInactive
              }`}>
                {selectedStock['Durum'] === 'aktif' ? 'Aktif' : 'İnaktif'}
              </span>
            </div>

            <div className={styles.actionButtons}>
              <button className={`${styles.actionButton} ${styles.editButton}`} onClick={handleOpenEditModal}>
                <FaEdit className={styles.actionIcon} />
                <span>Düzenle</span>
              </button>

              <button
                className={`${styles.actionButton} ${
                  selectedStock['Kategori'] === 'sarf' ? styles.categoryToggleSarf : styles.categoryToggleDemirbas
                }`}
                onClick={handleToggleCategory}
                title={selectedStock['Kategori'] === 'sarf' ? 'Demirbaşa Çevir' : 'Sarfa Çevir'}
              >
                <FaExchangeAlt className={styles.actionIcon} />
                <span>{selectedStock['Kategori'] === 'sarf' ? 'Demirbaşa Çevir' : 'Sarfa Çevir'}</span>
              </button>

              <button className={`${styles.actionButton} ${styles.statusButton}`} onClick={handleToggleStatus}>
                <FaExchangeAlt className={styles.actionIcon} />
                <span>Durum Değiştir</span>
              </button>

              <button className={`${styles.actionButton} ${styles.movementButton}`} onClick={handleOpenMovementModal}>
                <FaArrowsAltH className={styles.actionIcon} />
                <span>Stok Ata</span>
              </button>

              <button className={`${styles.actionButton} ${styles.historyButton}`} onClick={handleOpenHistoryModal}>
                <FaHistory className={styles.actionIcon} />
                <span>Stok Hareketleri</span>
              </button>
            </div>
          </div>
        )}

        <section className={`${styles.tableSection} ${isDarkMode ? 'dark-mode' : ''}`}>
          {/* Pagination Controls */}
          <div className={styles.paginationContainer}>
            <div className={styles.paginationInfo}>
              <span>
                Sayfa {currentPage} / {totalPages || 1} ({filteredStocks.length} toplam stok)
              </span>
            </div>
            <div className={styles.paginationControls}>
              <button
                className={styles.paginationButton}
                onClick={() => goToPage(1)}
                disabled={currentPage === 1}
              >
                İlk
              </button>
              <button
                className={styles.paginationButton}
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
              >
                <FaChevronLeft /> Önceki
              </button>
              <span className={styles.pageNumbers}>
                {Array.from({ length: Math.min(5, totalPages || 1) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      className={`${styles.pageNumber} ${currentPage === pageNum ? styles.activePage : ''}`}
                      onClick={() => goToPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </span>
              <button
                className={styles.paginationButton}
                onClick={goToNextPage}
                disabled={currentPage === totalPages || totalPages === 0}
              >
                Sonraki <FaChevronRight />
              </button>
              <button
                className={styles.paginationButton}
                onClick={() => goToPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
              >
                Son
              </button>
            </div>
          </div>

          <div className={`${styles.tableWrapper} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={`${styles.tableScrollContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
              {showLeftScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollLeft}`}
                  onClick={() => scrollTable('left')}
                >
                  <FaChevronLeft />
                </button>
              )}
              {showRightScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollRight}`}
                  onClick={() => scrollTable('right')}
                >
                  <FaChevronRight />
                </button>
              )}
              <div
                ref={tableContainerRef}
                className={`${styles.tableContainer} ${isDarkMode ? 'dark-mode' : ''}`}
                onScroll={handleScroll}
                onWheel={(e) => e.stopPropagation()}
              >
                <table className={`${styles.table} ${isDarkMode ? 'dark-mode' : ''}`}>
                  <thead>
                    <tr>
                      {Object.entries(columns).map(([key, col]) => 
                        visibleColumns[key] && (
                          <th key={key}>
                            {col.label}
                            <TableFilter 
                              column={key} 
                              type={col.type} 
                              options={col.options} 
                              onFilter={handleFilter}
                              currentValue={filters[key] || ''} 
                            />
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedStocks.length > 0 ? (
                      paginatedStocks.map((stock) => {
                        const isStockLow = isLowStock(stock);
                        return (
                          <tr 
                            key={stock.id} 
                            onClick={() => handleStockSelect(stock)}
                            className={`
                              ${selectedStock && selectedStock.id === stock.id ? styles.selectedRow : ''}
                              ${isStockLow ? styles.lowStockRow : ''}
                            `.trim()}
                          >
                          {Object.keys(columns).map(key =>
                            visibleColumns[key] && (
                              <td key={key} title={stock[key]} className={key === 'Kalan' && isStockLow ? styles.lowStockWarning : ''}>
                                {key === 'Durum' ? (
                                  <span className={`${styles.statusBadge} ${
                                    stock[key] === 'aktif'
                                      ? styles.statusActive
                                      : styles.statusInactive
                                  }`}>
                                    {stock[key] === 'aktif' ? 'aktif' : 'inaktif'}
                                  </span>
                                ) : key === 'Kategori' ? (
                                  <span className={`${styles.categoryBadge} ${
                                    stock[key] === 'sarf'
                                      ? styles.categorySarf
                                      : stock[key] === 'demirbas'
                                        ? styles.categoryDemirbas
                                        : styles.categoryUnknown
                                  }`}>
                                    {stock[key] === 'sarf' ? (
                                      <><FaCubes style={{ marginRight: '4px' }} /> Sarf</>
                                    ) : stock[key] === 'demirbas' ? (
                                      <><FaTools style={{ marginRight: '4px' }} /> Demirbaş</>
                                    ) : '-'}
                                  </span>
                                ) : key === 'customerName' || key === 'projectName' ? (
                                  // Müşteri/proje bağı yalnızca sarf malzemesinde anlamlı
                                  stock['Kategori'] === 'sarf' && stock[key] ? (
                                    <span className={styles.scopeChip}>{truncateText(stock[key])}</span>
                                  ) : (
                                    <span className={styles.scopeEmpty}>—</span>
                                  )
                                ) : key === 'Kalan' && isStockLow ? (
                                  <span style={{ 
                                    color: '#ef4444', 
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}>
                                    <FaExclamationTriangle />
                                    {truncateText(stock[key])}
                                  </span>
                                ) : (
                                  truncateText(stock[key])
                                )}
                              </td>
                            )
                          )}
                        </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={Object.keys(visibleColumns).filter(key => visibleColumns[key]).length} className={styles.noDataCell}>
                          <div className={styles.noData}>
                            <FaBoxOpen className={styles.noDataIcon} />
                            <p>Filtrelere uygun ürün bulunamadı</p>
                            {(Object.keys(filters).length > 0 || searchTerm) && (
                              <button className={styles.clearFiltersButton} onClick={clearFilters}>
                                Filtreleri Temizle
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Yeni Stok Ekle Modal - Modern Design */}
      <Modal
        show={showAddModal}
        onHide={handleCloseAddModal}
        size="lg"
        centered
        className={styles.modernModal}
      >
        {/* Custom Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderContent}>
            <div className={styles.modalHeaderIcon}>
              <FaPlus />
            </div>
            <div>
              <h2 className={styles.modalTitle}>Yeni Stok Ekle</h2>
              <p className={styles.modalSubtitle}>Stok bilgilerini girerek yeni ürün ekleyin</p>
            </div>
          </div>
          <button className={styles.modalCloseBtn} onClick={handleCloseAddModal}>
            <FaTimes />
          </button>
        </div>

        <Modal.Body className={styles.modalBody}>
          {/* Category Selection */}
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <FaLayerGroup className={styles.sectionIcon} />
              <span>Kategori Seçimi</span>
              <span className={styles.requiredBadge}>Zorunlu</span>
            </div>
            <div className={styles.categoryCards}>
              <div
                className={`${styles.categoryCard} ${newStock['Kategori'] === 'sarf' ? styles.categoryCardSarfActive : ''}`}
                onClick={() => handleNewStockChange('Kategori', 'sarf')}
              >
                <div className={styles.categoryCardIcon}>
                  <FaCubes />
                </div>
                <div className={styles.categoryCardContent}>
                  <span className={styles.categoryCardTitle}>Sarf Malzemesi</span>
                  <span className={styles.categoryCardDesc}>Tüketim malzemeleri, son kullanma tarihli ürünler</span>
                </div>
                {newStock['Kategori'] === 'sarf' && (
                  <div className={styles.categoryCardCheck}>
                    <FaCheckCircle />
                  </div>
                )}
              </div>
              <div
                className={`${styles.categoryCard} ${newStock['Kategori'] === 'demirbas' ? styles.categoryCardDemirbasActive : ''}`}
                onClick={() => handleNewStockChange('Kategori', 'demirbas')}
              >
                <div className={styles.categoryCardIcon}>
                  <FaTools />
                </div>
                <div className={styles.categoryCardContent}>
                  <span className={styles.categoryCardTitle}>Demirbaş</span>
                  <span className={styles.categoryCardDesc}>Sabit kıymetler, ekipman ve uzun ömürlü varlıklar</span>
                </div>
                {newStock['Kategori'] === 'demirbas' && (
                  <div className={styles.categoryCardCheck}>
                    <FaCheckCircle />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <FaInfoCircle className={styles.sectionIcon} />
              <span>Temel Bilgiler</span>
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Ürün Adı <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder="Ürün adını girin"
                  value={newStock['Adı'] || ''}
                  onChange={e => handleNewStockChange('Adı', e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Stok Tipi</label>
                <select
                  className={styles.formSelect}
                  value={newStock['Stok Tipi'] || ''}
                  onChange={e => handleNewStockChange('Stok Tipi', e.target.value)}
                >
                  <option value="">Tip seçin</option>
                  {stockTypes
                    .filter(type => type.name !== 'Sarf Malzemesi' && type.name !== 'Demirbaş')
                    .map(type => (
                      <option key={type.id} value={type.name}>{type.name}</option>
                    ))}
                </select>
                {stockTypes.length === 0 && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    color: '#856404'
                  }}>
                    ⚠️ Henüz stok tipi eklenmemiş. Lütfen önce "Stok Tipleri" bölümünden stok tipi ekleyin.
                  </div>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Durum</label>
                <select
                  className={styles.formSelect}
                  value={newStock['Durum'] || ''}
                  onChange={e => handleNewStockChange('Durum', e.target.value)}
                >
                  <option value="">Durum seçin</option>
                  <option value="aktif">Aktif</option>
                  <option value="inaktif">İnaktif</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Bilgi / Açıklama</label>
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder="Ek bilgi girin"
                  value={newStock['Bilgi'] || ''}
                  onChange={e => handleNewStockChange('Bilgi', e.target.value)}
                />
              </div>
              {/* Sarf malzemesi bir müşteriye/projeye bağlanabilir; raporda
                  "hangi müşteriye ne kadar gitti" bu bağdan hesaplanır. */}
              {newStock['Kategori'] === 'sarf' && (
                <>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Müşteri</label>
                    <select
                      className={styles.formSelect}
                      value={newStock.customerName || ''}
                      onChange={e => setNewStock(applyScopeChange(newStock, 'customerName', e.target.value))}
                    >
                      <option value="">Müşteri seçin (opsiyonel)</option>
                      {customers.map(customer => (
                        <option key={customer.id} value={customer.name}>{customer.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Proje</label>
                    <select
                      className={styles.formSelect}
                      value={newStock.projectName || ''}
                      onChange={e => setNewStock(applyScopeChange(newStock, 'projectName', e.target.value))}
                    >
                      <option value="">Proje seçin (opsiyonel)</option>
                      {projectsForCustomer(newStock.customerName).map(project => (
                        <option key={project.id} value={project.name}>{project.name}</option>
                      ))}
                    </select>
                    {projects.length === 0 ? (
                      <span className={styles.scopeHint}>Proje listesi yüklenemedi.</span>
                    ) : hasNoMatchingProject(newStock.customerName) ? (
                      <span className={styles.scopeHint}>
                        Bu müşteriye bağlı proje bulunamadı; tüm projeler listeleniyor.
                      </span>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Stock & Warehouse */}
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <FaWarehouse className={styles.sectionIcon} />
              <span>Stok & Depo</span>
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Miktar <span className={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  className={styles.formInput}
                  placeholder="0"
                  value={newStock['Miktar'] || ''}
                  onChange={e => handleNewStockChange('Miktar', e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Kalan Miktar</label>
                <input
                  type="number"
                  className={styles.formInput}
                  placeholder="0"
                  value={newStock['Kalan'] || ''}
                  onChange={e => handleNewStockChange('Kalan', e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Min Miktar</label>
                <input
                  type="number"
                  className={styles.formInput}
                  placeholder="1"
                  min="0"
                  value={newStock['Min Miktar'] ?? '1'}
                  onChange={e => handleNewStockChange('Min Miktar', e.target.value)}
                />
                <small className={styles.formHint}>Stok bu değerin altına düşünce uyarı verilir</small>
              </div>
              <div className={styles.formGroupFull}>
                <label className={styles.formLabel}>Depo Lokasyonu</label>
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder="Depo adı veya lokasyon"
                  value={newStock['Depo'] || ''}
                  onChange={e => handleNewStockChange('Depo', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <FaMoneyBillWave className={styles.sectionIcon} />
              <span>Fiyatlandırma</span>
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Giriş Fiyatı</label>
                <div className={styles.inputWithIcon}>
                  <input
                    type="number"
                    className={styles.formInput}
                    placeholder="0.00"
                    value={newStock['Giriş Fiyatı'] || ''}
                    onChange={e => handleNewStockChange('Giriş Fiyatı', e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Çıkış Fiyatı</label>
                <div className={styles.inputWithIcon}>
                  <input
                    type="number"
                    className={styles.formInput}
                    placeholder="0.00"
                    value={newStock['Çıkış Fiyatı'] || ''}
                    onChange={e => handleNewStockChange('Çıkış Fiyatı', e.target.value)}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Döviz Tipi</label>
                <select
                  className={styles.formSelect}
                  value={newStock['Döviz Tipi'] || ''}
                  onChange={e => handleNewStockChange('Döviz Tipi', e.target.value)}
                >
                  <option value="">Seçin</option>
                  <option value="TRY">TRY - Türk Lirası</option>
                  <option value="USD">USD - Amerikan Doları</option>
                  <option value="EUR">EUR - Euro</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Döviz Kuru</label>
                <input
                  type="number"
                  className={styles.formInput}
                  placeholder="1.00"
                  value={newStock['Döviz Kuru'] || ''}
                  onChange={e => handleNewStockChange('Döviz Kuru', e.target.value)}
                />
              </div>
              {newStock['Kar Oranı(%)'] && (
                <div className={styles.profitDisplay}>
                  <span className={styles.profitLabel}>Kar Oranı</span>
                  <span className={`${styles.profitValue} ${parseFloat(newStock['Kar Oranı(%)']) >= 0 ? styles.profitPositive : styles.profitNegative}`}>
                    %{newStock['Kar Oranı(%)']}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <FaCalendarAlt className={styles.sectionIcon} />
              <span>Tarih Bilgileri</span>
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Kullanma Başlama Tarihi</label>
                <input
                  type="date"
                  className={styles.formInput}
                  value={newStock['Kullanma Başlama Tarihi'] || ''}
                  onChange={e => handleNewStockChange('Kullanma Başlama Tarihi', e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Son Kullanma Tarihi</label>
                <input
                  type="date"
                  className={styles.formInput}
                  value={newStock['Son Kullanma Tarihi'] || ''}
                  onChange={e => handleNewStockChange('Son Kullanma Tarihi', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Additional Info - Only for Demirbaş */}
          {newStock['Kategori'] === 'demirbas' && (
            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <FaClipboardList className={styles.sectionIcon} />
                <span>Demirbaş Bilgileri</span>
              </div>
              <div className={styles.formGrid}>
                <div className={styles.formGroupFull}>
                  <label className={styles.formLabel}>Yıpranma Payı (%)</label>
                  <input
                    type="number"
                    className={styles.formInput}
                    placeholder="Yıllık amortisman oranı"
                    value={newStock['Yıpranma Payı'] || ''}
                    onChange={e => handleNewStockChange('Yıpranma Payı', e.target.value)}
                  />
                  <small className={styles.formHint}>Demirbaş için yıllık değer kaybı oranını girin</small>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        {/* Custom Footer */}
        <div className={styles.modalFooter}>
          <button className={styles.btnCancel} onClick={handleCloseAddModal}>
            <FaTimes /> İptal
          </button>
          <button className={styles.btnSave} onClick={handleAddStock}>
            <FaCheckCircle /> Stok Ekle
          </button>
        </div>
      </Modal>

      {/* Stok Geçmişi Modal */}
      <Modal show={showHistoryModal} onHide={handleCloseHistoryModal} size="xl" style={{ maxWidth: '90%' }}>
        <Modal.Header closeButton style={{ display: 'flex', alignItems: 'center' }}>
          <Modal.Title style={{ flex: 1 }}>Stok Hareketleri - {selectedStock?.['Adı']}</Modal.Title>
          <div style={{ position: 'absolute', right: '50px' }}>
            <button 
              className={`${styles.actionButton} ${styles.downloadButton}`}
              onClick={handleExportStockHistory}
              disabled={stockHistory.length === 0}
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px',
                cursor: stockHistory.length === 0 ? 'not-allowed' : 'pointer',
                opacity: stockHistory.length === 0 ? 0.5 : 1
              }}
            >
              <FaDownload />
              Excel İndir
            </button>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div style={{ padding: '10px 20px' }}>
            {stockHistory.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px', color: '#6c757d' }}>
                <FaHistory style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }} />
                <p>Bu stok için henüz hareket kaydı bulunmuyor.</p>
              </div>
            ) : (
              <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead style={{
                    position: 'sticky',
                    top: 0,
                    backgroundColor: isDarkMode ? '#1a202c' : '#f8f9fa',
                    zIndex: 1
                  }}>
                    <tr>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Tarih</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Çalışan/Ziyaret</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Miktar</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Tip</th>
                      <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Not</th>
                      <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>İşlem</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockHistory.map((movement, index) => {
                      const isVisitMovement = movement.type === 'visit_usage' || movement.type === 'visit_return';
                      const isReturn = movement.type === 'visit_return' || movement.quantity < 0;
                      const displayQuantity = Math.abs(movement.quantity);

                      return (
                        <tr key={movement.id} style={{
                          backgroundColor: index % 2 === 0
                            ? (isDarkMode ? '#2d3748' : '#ffffff')
                            : (isDarkMode ? '#1a202c' : '#f8f9fa')
                        }}>
                          <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                            {formatDateTime(movement.date)}
                          </td>
                          <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                            {isVisitMovement ? (
                              <div>
                                <div style={{ fontWeight: 'bold' }}>
                                  {movement.employeeName || 'Bilinmeyen Çalışan'}
                                </div>
                                <div style={{ fontSize: '0.85em', color: '#6c757d' }}>
                                  Ziyaret: {movement.patientId || 'Hasta ID yok'}
                                </div>
                              </div>
                            ) : (
                              movement.employeeName || '-'
                            )}
                          </td>
                          <td style={{
                            padding: '12px',
                            textAlign: 'center',
                            borderBottom: '1px solid #dee2e6',
                            fontWeight: 'bold',
                            color: isReturn ? '#28a745' : '#dc3545'
                          }}>
                            {isReturn ? '+' : '-'}{displayQuantity}
                          </td>
                          <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                            {movement.type === 'visit_usage' && (
                              <span style={{ 
                                backgroundColor: '#e3f2fd', 
                                color: '#1976d2', 
                                padding: '2px 8px', 
                                borderRadius: '12px', 
                                fontSize: '0.85em' 
                              }}>
                                Ziyaret Kullanımı
                              </span>
                            )}
                            {movement.type === 'visit_return' && (
                              <span style={{ 
                                backgroundColor: '#e8f5e8', 
                                color: '#2e7d32', 
                                padding: '2px 8px', 
                                borderRadius: '12px', 
                                fontSize: '0.85em' 
                              }}>
                                Ziyaret İadesi
                              </span>
                            )}
                            {movement.type === 'assignment' && (
                              <span style={{ 
                                backgroundColor: '#fff3e0', 
                                color: '#f57c00', 
                                padding: '2px 8px', 
                                borderRadius: '12px', 
                                fontSize: '0.85em' 
                              }}>
                                Çalışan Ataması
                              </span>
                            )}
                            {!movement.type && (
                              <span style={{ 
                                backgroundColor: '#f5f5f5', 
                                color: '#757575', 
                                padding: '2px 8px', 
                                borderRadius: '12px', 
                                fontSize: '0.85em' 
                              }}>
                                Genel
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '12px', borderBottom: '1px solid #dee2e6' }}>
                            {movement.note || '-'}
                          </td>
                          <td style={{ 
                            padding: '12px', 
                            borderBottom: '1px solid #dee2e6',
                            textAlign: 'center'
                          }}>
                            {/* Only show "Geri Al" button for assignment type movements */}
                            {movement.type === 'assignment' && (
                              <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleUndoMovement(movement)}
                                title="Stoğu geri al"
                              >
                                Geri Al
                              </Button>
                            )}
                            {movement.type !== 'assignment' && (
                              <span style={{ color: '#6c757d', fontSize: '0.85em' }}>
                                {isVisitMovement ? 'Ziyaret Hareketi' : 'İşlem Yok'}
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseHistoryModal}>
            Kapat
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Stok Hareketi Modal */}
      <Modal show={showMovementModal} onHide={handleCloseMovementModal} size="md">
        <Modal.Header closeButton>
          <Modal.Title>Stok Ata - {selectedStock?.['Adı']}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ padding: '10px 20px' }}>
            {/* Mevcut Stok Bilgisi */}
            <div style={{
              backgroundColor: isDarkMode ? '#2d3748' : '#f8f9fa',
              padding: '15px',
              borderRadius: '8px',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ fontWeight: '600' }}>Toplam Miktar:</span>
                <span>{selectedStock?.['Miktar'] || 0}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                <span style={{ fontWeight: '600', color: '#28a745' }}>Kalan Miktar:</span>
                <span style={{ fontWeight: 'bold', color: '#28a745' }}>{selectedStock?.['Kalan'] || 0}</span>
              </div>
            </div>

            {/* Atama Formu */}
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Çalışan Seç *</Form.Label>
                <Form.Select
                  value={movementData.employeeId}
                  onChange={(e) => setMovementData({ ...movementData, employeeId: e.target.value })}
                >
                  <option value="">Bir çalışan seçin...</option>
                  {activeEmployees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.eName} {emp.eSurname || ''}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Miktar *</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  step="1"
                  placeholder="Atanacak miktar"
                  value={movementData.quantity}
                  onChange={(e) => setMovementData({ ...movementData, quantity: e.target.value })}
                />
                <Form.Text className="text-muted">
                  Maksimum: {selectedStock?.['Kalan'] || 0}
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Not</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Opsiyonel not ekleyin..."
                  value={movementData.note}
                  onChange={(e) => setMovementData({ ...movementData, note: e.target.value })}
                />
              </Form.Group>
            </Form>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseMovementModal}>
            İptal
          </Button>
          <Button variant="primary" onClick={handleSaveMovement}>
            Ata
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Stok Düzenle Modal - Modern Design */}
      <Modal
        show={showEditModal}
        onHide={handleCloseEditModal}
        size="lg"
        centered
        className={styles.modernModal}
      >
        {/* Custom Header */}
        <div className={styles.modalHeader} style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
          <div className={styles.modalHeaderContent}>
            <div className={styles.modalHeaderIcon}>
              <FaEdit />
            </div>
            <div>
              <h2 className={styles.modalTitle}>Stok Düzenle</h2>
              <p className={styles.modalSubtitle}>{editStock['Adı'] || 'Stok bilgilerini güncelleyin'}</p>
            </div>
          </div>
          <button className={styles.modalCloseBtn} onClick={handleCloseEditModal}>
            <FaTimes />
          </button>
        </div>

        <Modal.Body className={styles.modalBody}>
          {/* Category Selection */}
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <FaLayerGroup className={styles.sectionIcon} />
              <span>Kategori Seçimi</span>
              <span className={styles.requiredBadge}>Zorunlu</span>
            </div>
            <div className={styles.categoryCards}>
              <div
                className={`${styles.categoryCard} ${editStock['Kategori'] === 'sarf' ? styles.categoryCardSarfActive : ''}`}
                onClick={() => handleEditStockChange('Kategori', 'sarf')}
              >
                <div className={styles.categoryCardIcon}>
                  <FaCubes />
                </div>
                <div className={styles.categoryCardContent}>
                  <span className={styles.categoryCardTitle}>Sarf Malzemesi</span>
                  <span className={styles.categoryCardDesc}>Tüketim malzemeleri, son kullanma tarihli ürünler</span>
                </div>
                {editStock['Kategori'] === 'sarf' && (
                  <div className={styles.categoryCardCheck}>
                    <FaCheckCircle />
                  </div>
                )}
              </div>
              <div
                className={`${styles.categoryCard} ${editStock['Kategori'] === 'demirbas' ? styles.categoryCardDemirbasActive : ''}`}
                onClick={() => handleEditStockChange('Kategori', 'demirbas')}
              >
                <div className={styles.categoryCardIcon}>
                  <FaTools />
                </div>
                <div className={styles.categoryCardContent}>
                  <span className={styles.categoryCardTitle}>Demirbaş</span>
                  <span className={styles.categoryCardDesc}>Sabit kıymetler, ekipman ve uzun ömürlü varlıklar</span>
                </div>
                {editStock['Kategori'] === 'demirbas' && (
                  <div className={styles.categoryCardCheck}>
                    <FaCheckCircle />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <FaInfoCircle className={styles.sectionIcon} />
              <span>Temel Bilgiler</span>
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Ürün Adı <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder="Ürün adını girin"
                  value={editStock['Adı'] || ''}
                  onChange={e => handleEditStockChange('Adı', e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Stok Tipi</label>
                <select
                  className={styles.formSelect}
                  value={editStock['Stok Tipi'] || ''}
                  onChange={e => handleEditStockChange('Stok Tipi', e.target.value)}
                >
                  <option value="">Tip seçin</option>
                  {stockTypes
                    .filter(type => type.name !== 'Sarf Malzemesi' && type.name !== 'Demirbaş')
                    .map(type => (
                      <option key={type.id} value={type.name}>{type.name}</option>
                    ))}
                </select>
                {stockTypes.length === 0 && (
                  <div style={{
                    marginTop: '8px',
                    padding: '8px 12px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '4px',
                    fontSize: '0.85rem',
                    color: '#856404'
                  }}>
                    ⚠️ Henüz stok tipi eklenmemiş. Lütfen önce "Stok Tipleri" bölümünden stok tipi ekleyin.
                  </div>
                )}
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Durum</label>
                <select
                  className={styles.formSelect}
                  value={editStock['Durum'] || ''}
                  onChange={e => handleEditStockChange('Durum', e.target.value)}
                >
                  <option value="">Durum seçin</option>
                  <option value="aktif">Aktif</option>
                  <option value="inaktif">İnaktif</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Bilgi / Açıklama</label>
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder="Ek bilgi girin"
                  value={editStock['Bilgi'] || ''}
                  onChange={e => handleEditStockChange('Bilgi', e.target.value)}
                />
              </div>
              {editStock['Kategori'] === 'sarf' && (
                <>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Müşteri</label>
                    <select
                      className={styles.formSelect}
                      value={editStock.customerName || ''}
                      onChange={e => setEditStock(applyScopeChange(editStock, 'customerName', e.target.value))}
                    >
                      <option value="">Müşteri seçin (opsiyonel)</option>
                      {customers.map(customer => (
                        <option key={customer.id} value={customer.name}>{customer.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Proje</label>
                    <select
                      className={styles.formSelect}
                      value={editStock.projectName || ''}
                      onChange={e => setEditStock(applyScopeChange(editStock, 'projectName', e.target.value))}
                    >
                      <option value="">Proje seçin (opsiyonel)</option>
                      {projectsForCustomer(editStock.customerName).map(project => (
                        <option key={project.id} value={project.name}>{project.name}</option>
                      ))}
                    </select>
                    {projects.length === 0 ? (
                      <span className={styles.scopeHint}>Proje listesi yüklenemedi.</span>
                    ) : hasNoMatchingProject(editStock.customerName) ? (
                      <span className={styles.scopeHint}>
                        Bu müşteriye bağlı proje bulunamadı; tüm projeler listeleniyor.
                      </span>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Stock & Warehouse */}
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <FaWarehouse className={styles.sectionIcon} />
              <span>Stok & Depo</span>
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>
                  Miktar <span className={styles.required}>*</span>
                </label>
                <input
                  type="number"
                  className={styles.formInput}
                  placeholder="0"
                  value={editStock['Miktar'] || ''}
                  onChange={e => handleEditStockChange('Miktar', e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Kalan Miktar</label>
                <input
                  type="number"
                  className={styles.formInput}
                  placeholder="0"
                  value={editStock['Kalan'] || ''}
                  onChange={e => handleEditStockChange('Kalan', e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Min Miktar</label>
                <input
                  type="number"
                  className={styles.formInput}
                  placeholder="1"
                  min="0"
                  value={editStock['Min Miktar'] ?? '1'}
                  onChange={e => handleEditStockChange('Min Miktar', e.target.value)}
                />
                <small className={styles.formHint}>Stok bu değerin altına düşünce uyarı verilir</small>
              </div>
              <div className={styles.formGroupFull}>
                <label className={styles.formLabel}>Depo Lokasyonu</label>
                <input
                  type="text"
                  className={styles.formInput}
                  placeholder="Depo adı veya lokasyon"
                  value={editStock['Depo'] || ''}
                  onChange={e => handleEditStockChange('Depo', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <FaMoneyBillWave className={styles.sectionIcon} />
              <span>Fiyatlandırma</span>
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Giriş Fiyatı</label>
                <input
                  type="number"
                  className={styles.formInput}
                  placeholder="0.00"
                  value={editStock['Giriş Fiyatı'] || ''}
                  onChange={e => handleEditStockChange('Giriş Fiyatı', e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Çıkış Fiyatı</label>
                <input
                  type="number"
                  className={styles.formInput}
                  placeholder="0.00"
                  value={editStock['Çıkış Fiyatı'] || ''}
                  onChange={e => handleEditStockChange('Çıkış Fiyatı', e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Döviz Tipi</label>
                <select
                  className={styles.formSelect}
                  value={editStock['Döviz Tipi'] || ''}
                  onChange={e => handleEditStockChange('Döviz Tipi', e.target.value)}
                >
                  <option value="">Seçin</option>
                  <option value="TRY">TRY - Türk Lirası</option>
                  <option value="USD">USD - Amerikan Doları</option>
                  <option value="EUR">EUR - Euro</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Döviz Kuru</label>
                <input
                  type="number"
                  className={styles.formInput}
                  placeholder="1.00"
                  value={editStock['Döviz Kuru'] || ''}
                  onChange={e => handleEditStockChange('Döviz Kuru', e.target.value)}
                />
              </div>
              {editStock['Kar Oranı(%)'] && (
                <div className={styles.profitDisplay}>
                  <span className={styles.profitLabel}>Kar Oranı</span>
                  <span className={`${styles.profitValue} ${parseFloat(editStock['Kar Oranı(%)']) >= 0 ? styles.profitPositive : styles.profitNegative}`}>
                    %{editStock['Kar Oranı(%)']}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className={styles.formSection}>
            <div className={styles.sectionHeader}>
              <FaCalendarAlt className={styles.sectionIcon} />
              <span>Tarih Bilgileri</span>
            </div>
            <div className={styles.formGrid}>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Kullanma Başlama Tarihi</label>
                <input
                  type="date"
                  className={styles.formInput}
                  value={editStock['Kullanma Başlama Tarihi'] || ''}
                  onChange={e => handleEditStockChange('Kullanma Başlama Tarihi', e.target.value)}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Son Kullanma Tarihi</label>
                <input
                  type="date"
                  className={styles.formInput}
                  value={editStock['Son Kullanma Tarihi'] || ''}
                  onChange={e => handleEditStockChange('Son Kullanma Tarihi', e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Additional Info - Only for Demirbaş */}
          {editStock['Kategori'] === 'demirbas' && (
            <div className={styles.formSection}>
              <div className={styles.sectionHeader}>
                <FaClipboardList className={styles.sectionIcon} />
                <span>Demirbaş Bilgileri</span>
              </div>
              <div className={styles.formGrid}>
                <div className={styles.formGroupFull}>
                  <label className={styles.formLabel}>Yıpranma Payı (%)</label>
                  <input
                    type="number"
                    className={styles.formInput}
                    placeholder="Yıllık amortisman oranı"
                    value={editStock['Yıpranma Payı'] || ''}
                    onChange={e => handleEditStockChange('Yıpranma Payı', e.target.value)}
                  />
                  <small className={styles.formHint}>Demirbaş için yıllık değer kaybı oranını girin</small>
                </div>
              </div>
            </div>
          )}
        </Modal.Body>

        {/* Custom Footer */}
        <div className={styles.modalFooter}>
          <button className={styles.btnCancel} onClick={handleCloseEditModal}>
            <FaTimes /> İptal
          </button>
          <button className={styles.btnSave} style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }} onClick={handleUpdateStock}>
            <FaCheckCircle /> Güncelle
          </button>
        </div>
      </Modal>

      {/* Stok Tipi Ekle Modal */}
      <Modal show={showStockTypeModal} onHide={handleCloseStockTypeModal} size="md">
        <Modal.Header closeButton>
          <Modal.Title>Stok Tipi Ekle</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ padding: '10px 20px' }}>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Stok Tipi Adı *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Örn: Elektronik, Gıda, Temizlik..."
                  value={newStockType}
                  onChange={(e) => setNewStockType(e.target.value)}
                />
              </Form.Group>
            </Form>

            {/* Mevcut Stok Tipleri Listesi */}
            {stockTypes.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <h6 style={{ marginBottom: '10px', color: '#6c757d' }}>Mevcut Stok Tipleri:</h6>
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  maxHeight: '150px',
                  overflowY: 'auto',
                  padding: '10px',
                  backgroundColor: isDarkMode ? '#2d3748' : '#f8f9fa',
                  borderRadius: '8px'
                }}>
                  {stockTypes
                    .filter(type => type.name !== 'Sarf Malzemesi' && type.name !== 'Demirbaş')
                    .map(type => {
                      const stockCount = stocks.filter(s => s['Stok Tipi'] === type.name).length;
                      return (
                        <span
                          key={type.id}
                          style={{
                            padding: '6px 12px',
                            backgroundColor: isDarkMode ? '#4a5568' : '#e9ecef',
                            borderRadius: '16px',
                            fontSize: '14px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            marginRight: '8px',
                            marginBottom: '8px'
                          }}
                        >
                          {type.name}
                          {stockCount === 0 && (
                            <button
                              onClick={(e) => handleDeleteStockType(e, type.id, type.name)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#dc3545',
                                cursor: 'pointer',
                                padding: '0',
                                marginLeft: '4px',
                                fontSize: '16px',
                                display: 'flex',
                                alignItems: 'center'
                              }}
                              title="Sil"
                            >
                              ×
                            </button>
                          )}
                          {stockCount > 0 && (
                            <span
                              style={{
                                fontSize: '12px',
                                color: isDarkMode ? '#a0aec0' : '#6c757d',
                                marginLeft: '4px'
                              }}
                            >
                              ({stockCount})
                            </span>
                          )}
                        </span>
                      );
                    })}
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleCloseStockTypeModal}>
            İptal
          </Button>
          <Button variant="primary" onClick={handleAddStockType}>
            Ekle
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ─── Atanan Stoklar Modal ─────────────────────────────────────────── */}
      <Modal
        show={showAssignedStocksModal}
        onHide={() => setShowAssignedStocksModal(false)}
        size="xl"
        centered
        className={styles.modernModal}
      >
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderContent}>
            <div className={`${styles.modalHeaderIcon} ${styles.assignedModalIcon}`}>
              <FaUsers />
            </div>
            <div>
              <h2 className={styles.modalTitle}>Atanan Stoklar</h2>
              <p className={styles.modalSubtitle}>Çalışanlara atanmış stok ürünlerinin özeti</p>
            </div>
          </div>
          <button className={styles.modalCloseBtn} onClick={() => setShowAssignedStocksModal(false)}>
            <FaTimes />
          </button>
        </div>

        <Modal.Body className={`${styles.modalBody} ${styles.assignedModalBody}`}>
          {assignedStocksLoading ? (
            <div className={styles.assignedLoadingState}>
              <div className={styles.assignedSpinner}></div>
              <p>Atama verileri yükleniyor...</p>
            </div>
          ) : (
            <>
              {/* Summary Stats */}
              {(() => {
                const totalAssignments = assignedStocksData.byEmployee.reduce(
                  (sum, e) => sum + e.assignments.length, 0
                );
                const totalQty = assignedStocksData.byEmployee.reduce(
                  (sum, e) => sum + e.assignments.reduce((s, a) => s + (parseFloat(a.quantity) || 0), 0), 0
                );
                // "Çalışan" kartı eskiden olduğu gibi yalnızca ataması olanları
                // sayar; atama yapılmamış çalışanlar ayrı kartta gösterilir.
                const withAssignment = assignedStocksData.byEmployee.filter(e => e.assignments.length > 0).length;
                const withoutAssignment = assignedStocksData.byEmployee.length - withAssignment;
                return (
                  <div className={styles.assignedSummaryBar}>
                    <div className={styles.assignedSummaryCard}>
                      <span className={`${styles.assignedSummaryIcon} ${styles.summaryIconBlue}`}>
                        <FaClipboardList />
                      </span>
                      <div>
                        <p className={styles.assignedSummaryValue}>{totalAssignments}</p>
                        <p className={styles.assignedSummaryLabel}>Toplam Atama</p>
                      </div>
                    </div>
                    <div className={styles.assignedSummaryCard}>
                      <span className={`${styles.assignedSummaryIcon} ${styles.summaryIconPurple}`}>
                        <FaUsers />
                      </span>
                      <div>
                        <p className={styles.assignedSummaryValue}>{withAssignment}</p>
                        <p className={styles.assignedSummaryLabel}>Çalışan</p>
                      </div>
                    </div>
                    <div className={styles.assignedSummaryCard}>
                      <span className={`${styles.assignedSummaryIcon} ${styles.summaryIconGray}`}>
                        <FaUserSlash />
                      </span>
                      <div>
                        <p className={styles.assignedSummaryValue}>{withoutAssignment}</p>
                        <p className={styles.assignedSummaryLabel}>Atama Yok</p>
                      </div>
                    </div>
                    <div className={styles.assignedSummaryCard}>
                      <span className={`${styles.assignedSummaryIcon} ${styles.summaryIconGreen}`}>
                        <FaBoxOpen />
                      </span>
                      <div>
                        <p className={styles.assignedSummaryValue}>{assignedStocksData.byStock.length}</p>
                        <p className={styles.assignedSummaryLabel}>Farklı Ürün</p>
                      </div>
                    </div>
                    <div className={styles.assignedSummaryCard}>
                      <span className={`${styles.assignedSummaryIcon} ${styles.summaryIconOrange}`}>
                        <FaLayerGroup />
                      </span>
                      <div>
                        <p className={styles.assignedSummaryValue}>{totalQty}</p>
                        <p className={styles.assignedSummaryLabel}>Toplam Miktar</p>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Search */}
              <div className={styles.assignedSearchWrapper}>
                <FaSearch className={styles.assignedSearchIcon} />
                <input
                  type="text"
                  className={styles.assignedSearchInput}
                  placeholder={assignedStocksViewMode === 'employee'
                    ? 'Çalışan adı veya stok adıyla ara...'
                    : 'Stok adı veya çalışan adıyla ara...'}
                  value={assignedStocksSearch}
                  onChange={e => setAssignedStocksSearch(e.target.value)}
                />
                {assignedStocksSearch && (
                  <button className={styles.assignedSearchClear} onClick={() => setAssignedStocksSearch('')}>
                    <FaTimes />
                  </button>
                )}
              </div>

              {/* View Mode Tabs */}
              <div className={styles.assignedViewTabs}>
                <button
                  className={`${styles.assignedViewTab} ${assignedStocksViewMode === 'employee' ? styles.assignedViewTabActive : ''}`}
                  onClick={() => setAssignedStocksViewMode('employee')}
                >
                  <FaUser style={{ marginRight: '6px' }} />
                  Çalışana Göre
                  <span className={styles.assignedTabBadge}>{assignedStocksData.byEmployee.length}</span>
                </button>
                <button
                  className={`${styles.assignedViewTab} ${assignedStocksViewMode === 'stock' ? styles.assignedViewTabActive : ''}`}
                  onClick={() => setAssignedStocksViewMode('stock')}
                >
                  <FaBoxOpen style={{ marginRight: '6px' }} />
                  Stoğa Göre
                  <span className={styles.assignedTabBadge}>{assignedStocksData.byStock.length}</span>
                </button>
              </div>

              {/* ── By Employee View ── */}
              {assignedStocksViewMode === 'employee' && (() => {
                const searchLower = assignedStocksSearch.toLocaleLowerCase('tr');
                const withAssignment = assignedStocksData.byEmployee.filter(e => e.assignments.length > 0).length;
                const withoutAssignment = assignedStocksData.byEmployee.length - withAssignment;

                const filtered = assignedStocksData.byEmployee.filter(emp => {
                  const hasAssignment = emp.assignments.length > 0;
                  if (assignedEmployeeFilter === 'assigned' && !hasAssignment) return false;
                  if (assignedEmployeeFilter === 'unassigned' && hasAssignment) return false;
                  if (!searchLower) return true;
                  return emp.employeeName.toLocaleLowerCase('tr').includes(searchLower) ||
                    emp.assignments.some(a => (a.stockName || '').toLocaleLowerCase('tr').includes(searchLower));
                });

                const filterChips = [
                  { key: 'all', label: 'Tümü', count: assignedStocksData.byEmployee.length },
                  { key: 'assigned', label: 'Ataması olanlar', count: withAssignment },
                  { key: 'unassigned', label: 'Atama yapılmayanlar', count: withoutAssignment }
                ];

                return (
                  <>
                    <div className={styles.assignedFilterChips}>
                      {filterChips.map(chip => (
                        <button
                          key={chip.key}
                          type="button"
                          className={`${styles.assignedFilterChip} ${
                            assignedEmployeeFilter === chip.key ? styles.assignedFilterChipActive : ''
                          }`}
                          onClick={() => setAssignedEmployeeFilter(chip.key)}
                        >
                          {chip.label}
                          <span className={styles.assignedChipCount}>{chip.count}</span>
                        </button>
                      ))}
                    </div>

                    {filtered.length === 0 ? (
                      <div className={styles.assignedEmptyState}>
                        <FaUser className={styles.emptyStateIcon} />
                        <p className={styles.emptyStateTitle}>Kayıt bulunamadı</p>
                        <p className={styles.emptyStateDesc}>
                          {assignedStocksSearch
                            ? 'Arama kriterlerine uygun kayıt yok.'
                            : assignedEmployeeFilter === 'unassigned'
                            ? 'Tüm çalışanlara en az bir stok atanmış.'
                            : 'Henüz hiçbir çalışana stok atanmamış.'}
                        </p>
                      </div>
                    ) : (
                  <div className={styles.assignedGrid}>
                    {filtered.map(emp => {
                      const color = getAvatarColor(emp.employeeName);
                      const initials = emp.employeeName.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
                      const totalQty = emp.assignments.reduce((s, a) => s + (parseFloat(a.quantity) || 0), 0);
                      const visibleAssignments = assignedStocksSearch
                        ? emp.assignments.filter(a => (a.stockName || '').toLocaleLowerCase('tr').includes(assignedStocksSearch.toLocaleLowerCase('tr')))
                        : emp.assignments;
                      const hasAssignment = emp.assignments.length > 0;
                      return (
                        <div
                          key={emp.employeeId}
                          className={`${styles.assignedEmployeeCard} ${
                            hasAssignment ? '' : styles.assignedEmployeeCardEmpty
                          }`}
                        >
                          <div className={styles.assignedEmployeeCardHeader}>
                            <div className={styles.assignedAvatar} style={{ backgroundColor: color }}>
                              {initials || <FaUser />}
                            </div>
                            <div className={styles.assignedEmployeeInfo}>
                              <span className={styles.assignedEmployeeName}>{emp.employeeName}</span>
                              <span className={styles.assignedEmployeeMeta}>
                                {hasAssignment
                                  ? <>{emp.assignments.length} atama &bull; {totalQty} adet toplam</>
                                  : 'Atama yok'}
                              </span>
                            </div>
                            <div className={styles.assignedEmployeeBadge} style={{ backgroundColor: color + '22', color }}>
                              {emp.assignments.length}
                            </div>
                          </div>
                          <div className={styles.assignedItemList}>
                            {hasAssignment ? (
                              visibleAssignments.map((a, idx) => (
                                <div key={a.id || idx} className={styles.assignedItem}>
                                  <div className={styles.assignedItemLeft}>
                                    <FaBoxOpen className={styles.assignedItemIcon} />
                                    <span className={styles.assignedItemName}>{a.stockName}</span>
                                  </div>
                                  <div className={styles.assignedItemRight}>
                                    <span className={styles.assignedItemQty}>{a.quantity} adet</span>
                                    <span className={styles.assignedItemDate}>{formatDateTime(a.date)}</span>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className={styles.assignedNoItems}>
                                <FaUserSlash className={styles.assignedNoItemsIcon} />
                                <span>Bu çalışana henüz stok atanmamış</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                    )}
                  </>
                );
              })()}

              {/* ── By Stock View ── */}
              {assignedStocksViewMode === 'stock' && (() => {
                const searchLower = assignedStocksSearch.toLocaleLowerCase('tr');
                const filtered = assignedStocksData.byStock.filter(s => {
                  if (!searchLower) return true;
                  return s.stockName.toLocaleLowerCase('tr').includes(searchLower) ||
                    s.assignments.some(a => (a.employeeName || '').toLocaleLowerCase('tr').includes(searchLower));
                });

                if (filtered.length === 0) {
                  return (
                    <div className={styles.assignedEmptyState}>
                      <FaBoxOpen className={styles.emptyStateIcon} />
                      <p className={styles.emptyStateTitle}>Atama bulunamadı</p>
                      <p className={styles.emptyStateDesc}>
                        {assignedStocksSearch ? 'Arama kriterlerine uygun kayıt yok.' : 'Henüz hiçbir stok atanmamış.'}
                      </p>
                    </div>
                  );
                }

                return (
                  <div className={styles.assignedGrid}>
                    {filtered.map(s => {
                      const totalQty = s.assignments.reduce((sum, a) => sum + (parseFloat(a.quantity) || 0), 0);
                      const visibleAssignments = assignedStocksSearch
                        ? s.assignments.filter(a => (a.employeeName || '').toLocaleLowerCase('tr').includes(assignedStocksSearch.toLocaleLowerCase('tr')))
                        : s.assignments;
                      return (
                        <div key={s.stockId} className={styles.assignedStockCard}>
                          <div className={styles.assignedStockCardHeader}>
                            <div className={styles.assignedStockIconBox}>
                              <FaCubes />
                            </div>
                            <div className={styles.assignedStockInfo}>
                              <span className={styles.assignedStockName}>{s.stockName}</span>
                              <span className={styles.assignedStockMeta}>
                                {s.assignments.length} çalışan &bull; {totalQty} adet dağıtıldı
                              </span>
                            </div>
                            <div className={styles.assignedStockQtyBadge}>
                              {totalQty} adet
                            </div>
                          </div>
                          <div className={styles.assignedItemList}>
                            {visibleAssignments.map((a, idx) => {
                              const color = getAvatarColor(a.employeeName);
                              const initials = a.employeeName.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
                              return (
                                <div key={a.id || idx} className={styles.assignedItem}>
                                  <div className={styles.assignedItemLeft}>
                                    <div className={styles.assignedMiniAvatar} style={{ backgroundColor: color }}>
                                      {initials || <FaUser />}
                                    </div>
                                    <span className={styles.assignedItemName}>{a.employeeName}</span>
                                  </div>
                                  <div className={styles.assignedItemRight}>
                                    <span className={styles.assignedItemQty}>{a.quantity} adet</span>
                                    <span className={styles.assignedItemDate}>{formatDateTime(a.date)}</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Kullanım raporu: kime ne kadar verildi, ne kullanıldı, ne kaldı */}
      <StockReportModal
        show={showReportModal}
        onClose={() => setShowReportModal(false)}
        isDarkMode={isDarkMode}
      />

      <ToastContainer
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme={isDarkMode ? "dark" : "light"}
      />
    </div>
  );
};

export default StockManagementPage;
